import { router } from "@/main";
import type {
	AcceptSuggestionRequest,
	ActionGroup,
	ActionGroupCreateRequest,
	ActionGroupListResponse,
	ActionGroupUpdateRequest,
	AddMembersResponse,
	AlertAction,
	AuditLogEntry,
	AuditLogListResponse,
	ContactGroupHistoryResponse,
	ContactGroupsResponse,
	ContactSearchResponse,
	FilterPreviewResponse,
	FilterRules,
	GroupMemberListResponse,
	GuidanceResponse,
	NLGroupConfirmResponse,
	NLGroupMessageResponse,
	NLGroupSessionResponse,
	NLGroupStartResponse,
	PendingAlertCountResponse,
	PendingAlertListResponse,
	PreActionSuggestionResponse,
	ResolvedAlertResponse,
	SuggestionSessionResponse,
	VoterStats,
} from "@/types/action-groups";
import type {
	BallotAccessProgress,
	DriveCreateRequest,
	DriveUpdateRequest,
	LookupRequirementsRequest,
	LookupRequirementsResponse,
	PetitionDrive,
	PetitionDriveDetail,
	PetitionSigner,
	SignatureRequirement,
	SignerCreateRequest,
	SignerListResponse,
	UpdateRequirementsRequest,
	UpdateRequirementsStatusRequest,
	ValidationResult,
} from "@/types/ballot-access";
import type {
	AutoReloadUpdateRequest,
	BillingRate,
	PaymentMethod,
	SendEstimate,
	SendEstimateRequest,
	SetupIntentResponse,
	TopUpIntentResponse,
	UsageQueryParams,
	UsageResponse,
	WalletResponse,
} from "@/types/billing";
import type {
	Briefing,
	BriefingItemUpdate,
	BriefingListParams,
	BriefingListResponse,
	BriefingStats,
	BulkBriefingItemUpdate,
	ConversationResponse,
	CreateFromSuggestionRequest,
	CreateFromSuggestionResponse,
	EmailBriefingSuggestion,
	EmailPerformanceSummary,
	PendingCheckResponse,
} from "@/types/briefing";
import type {
	CallerIdVerifyResponse,
	CallTimeEntry,
	CallTimeEntryCreate,
	CallTimeGoalResponse,
	CallTimeListEntry,
	CallTimeListParams,
	CallTimeStats,
	ContactCallHistoryResponse,
	ContactContext,
	DialRequest,
	DialResponse,
	PaginatedCallTimeListResponse,
	PaginatedRecipeResponse,
	Recipe,
	RecipeApplyResponse,
	RecipeCreateRequest,
	RecipeUpdateRequest,
	TranscriptionResponse,
	VoiceTokenResponse,
} from "@/types/call-time";
import type {
	CampaignGetResponse,
	CampaignGoalsPayload,
} from "@/types/campaign";
import type { CiceroOfficialsResponse } from "@/types/cicero";
import type {
	CampaignHealth,
	ColumnMappingResponse,
	Contact,
	ContactCountResponse,
	ContactCreate,
	ContactIntelligence,
	ContactInteraction,
	ContactListResponse,
	ContactTag,
	CSVImportJob,
	CSVUploadResponse,
	FollowUp,
	FollowUpListResponse,
	ImportStartResponse,
	PeopleListResponse,
	PipelineResponse,
} from "@/types/contacts";
import type { DashboardResponse } from "@/types/dashboard";
import type {
	DonationCreate,
	DonationFilters,
	DonationListResponse,
	DonationResponse,
	DonationSummary,
	DonationUpdate,
} from "@/types/donations";
import type {
	AudienceItem,
	AudiencePreviewResult,
	CampaignMetrics,
	CancelResult,
	DraftResult,
	EmailCampaign,
	EmailCampaignCreateRequest,
	EmailCampaignFilters,
	EmailCampaignListResponse,
	EmailCampaignUpdateRequest,
	EmailSettings,
	EmailTemplateListResponse,
	EmailTemplatePreview,
	GenerateDraftRequest,
	ProvisionEmailRequest,
	RecipientFilters,
	RecipientListResponse,
	RecipientPreviewResponse,
	ScheduleRequest,
	ScheduleResult,
	SendPreview,
	SendResult,
	UpdateEmailSettingsRequest,
} from "@/types/email-campaigns";
import type { GeocodeResponse } from "@/types/geocode";
import type { NbaResponse } from "@/types/nba";
import type {
	AddSendToGoalRequest,
	AudienceCountRequest,
	AudienceCountResponse,
	AudienceDefinition,
	ClarifyResponse,
	DraftRequest,
	DraftResponse,
	NlParseResponse,
	NlRefineResponse,
	OutreachAudience,
	OutreachAudienceCreate,
	OutreachAudienceUpdate,
	OutreachCampaign,
	OutreachCampaignCreate,
	OutreachCampaignUpdate,
	OutreachCostToDate,
	OutreachGoal,
	OutreachGoalCreate,
	OutreachGoalUpdate,
	ReviewResponse,
	SmartLinkMetricsResponse,
	SmartLinksResponse,
	SuggestionChip,
	TestSendResponse,
} from "@/types/outreach";
import type {
	ContactPhoneHistoryResponse,
	CreatePhoneCampaignRequest,
	CreatePhoneShiftRequest,
	DisposeCallRequest,
	GenerateScriptRequest,
	NextContactResponse,
	PhoneCampaign,
	PhoneCampaignListItem,
	PhoneCampaignResultsResponse,
	PhoneCampaignSummaryResponse,
	PhoneCampaignUpdate,
	PhoneScript,
	PhoneShift,
	QueueStats,
} from "@/types/phone-banking";
import type { AuthTokens, SessionData } from "@/types/session";
import type {
	CampaignStrategy,
	Correction,
	MagicLinkValidateResponse,
	RegenerateResponse,
	RevertResponse,
	SectionEditResponse,
	StartOverResponse,
	StrategyStatusResponse,
	VersionDetailResponse,
	VersionListResponse,
} from "@/types/strategy";
import type {
	BulkStatusUpdateResponse,
	Task,
	TaskCalendarResponse,
	TaskCreate,
	TaskListParams,
	TaskListResponse,
	TaskUpdate,
} from "@/types/tasks";
import type {
	BulkDeactivateRequest,
	BulkDeactivateResponse,
	CampaignUserListResponse,
	DeactivateRequest,
	DeactivateResponse,
	InviteListResponse,
	InviteResendResponse,
	InviteResponse,
	InviteRevokeResponse,
	InviteUserRequest,
	ReactivateRequest,
	ReactivateResponse,
	RoleChangeRequest,
	RoleChangeResponse,
	RoleHistoryResponse,
	TeamListParams,
} from "@/types/team";
import type {
	AudienceSnapshot,
	CampaignRepliesResponse,
	Conversation,
	P2PSendRequest,
	ResolveConversationRequest,
	SendQueueItem,
	TextCampaign,
	TextCampaignCreate,
	TextCampaignFilters,
	TextCampaignMetrics,
	TextCampaignUpdate,
	TextInteractionResponse,
	TextMessage,
	TextScript,
	TextScriptCreate,
	TextScriptUpdate,
	VolunteerReplyRequest,
} from "@/types/text-banking";
import type {
	PaginatedVolunteerResponse,
	VolunteerImportJobStatus,
	VolunteerImportResponse,
	VolunteerListParams,
	VolunteerProfile,
	VolunteerProfileCreate,
	VolunteerProfileUpdate,
} from "@/types/volunteers";
import type {
	XanderLightbulbState,
	XanderSession,
	XanderSessionListItem,
	XanderSuggestedPrompts,
} from "@/types/xander";
import { API } from "./api";
import { authService } from "./auth-service";
import { API_BASE_URL } from "./consts";

// ---------------------------------------------------------------------------
// Base API service — configurable auth + unauthorized handling
// ---------------------------------------------------------------------------

interface AuthServiceLike {
	getAuthToken(): Promise<string | null>;
	clearTokens(): void;
}

/**
 * Error thrown by the API client when a request fails. `.message` stays a
 * human-readable string (so existing `err instanceof Error ? err.message`
 * callers keep working), while `.status` (HTTP code) and `.detail` (the raw
 * parsed `detail` payload — which may be a structured object such as the
 * `{ code: "OUT_OF_CREDITS", ... }` body returned by the billing send-gate on a
 * 402) are preserved for callers that need to branch on them.
 */
export class ApiError extends Error {
	readonly status: number;
	readonly detail: unknown;

	constructor(message: string, status: number, detail: unknown) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.detail = detail;
	}
}

/** Friendly, consistent copy shown whenever the backend returns a 403. */
export const PERMISSION_DENIED_MESSAGE =
	"You do not have permission to perform this action.";

/** Pull a usable string out of a `detail` payload (string or structured object). */
function messageFromDetail(detail: unknown): string | undefined {
	if (typeof detail === "string" && detail) return detail;
	if (detail && typeof detail === "object") {
		const d = detail as { message?: unknown; error?: unknown; code?: unknown };
		if (typeof d.message === "string" && d.message) return d.message;
		if (typeof d.error === "string" && d.error) return d.error;
		if (typeof d.code === "string" && d.code) return d.code;
	}
	return undefined;
}

/**
 * Derive a human-readable message string from a backend error body.
 *
 * Handles both response shapes the API emits:
 *  - APIException envelope + raw FastAPI: `{ detail: <str|obj> }` (top level)
 *  - standardized HTTPException envelope: `{ message, error: { detail } }`
 *    whose generic `message` is the unhelpful "HTTP error occurred" — the real
 *    detail lives at `error.detail`.
 *
 * For 403s we always return a clear permission message instead of whatever
 * generic text the backend produced. Falls back to `message`, then status text.
 */
function errorMessageFromBody(
	body:
		| { detail?: unknown; message?: unknown; error?: { detail?: unknown } }
		| null
		| undefined,
	statusText: string,
	status?: number,
): string {
	if (status === 403) {
		// A plain-string `detail` is a specific, user-meaningful reason (e.g.
		// "Campaign does not have voter-file access enabled.") — surface it. The
		// structured RBAC denial ({error:"Insufficient permissions"}) and the
		// generic "HTTP error occurred" envelope get the friendly permission copy.
		const detail = body?.detail ?? body?.error?.detail;
		if (typeof detail === "string" && detail) return detail;
		return PERMISSION_DENIED_MESSAGE;
	}
	// top-level detail first (billing/APIException), then the HTTPException envelope
	const fromDetail =
		messageFromDetail(body?.detail) ?? messageFromDetail(body?.error?.detail);
	if (fromDetail) return fromDetail;
	// `message` is often the generic envelope label — only use it if meaningful
	if (
		typeof body?.message === "string" &&
		body.message &&
		body.message !== "HTTP error occurred"
	) {
		return body.message;
	}
	return statusText;
}

/**
 * Extract a structured `detail.code` from a thrown error, if present. Works on
 * `ApiError` (preferred) and defensively parses a stringified JSON `detail`
 * carried on a plain `Error.message` (in case some path still stringifies it).
 * Returns `undefined` when no code can be found.
 */
export function getApiErrorCode(err: unknown): string | undefined {
	if (err instanceof ApiError) {
		const detail = err.detail;
		if (detail && typeof detail === "object") {
			const code = (detail as { code?: unknown }).code;
			if (typeof code === "string") return code;
		}
	}
	if (err instanceof Error) {
		const trimmed = err.message.trim();
		if (trimmed.startsWith("{")) {
			try {
				const parsed = JSON.parse(trimmed) as { code?: unknown };
				if (typeof parsed.code === "string") return parsed.code;
			} catch {
				// not JSON — fall through
			}
		}
	}
	return undefined;
}

export class BaseApiService {
	protected baseURL = API_BASE_URL;
	private auth: AuthServiceLike;
	private onUnauthorized: () => void;

	constructor(auth: AuthServiceLike, onUnauthorized: () => void) {
		this.auth = auth;
		this.onUnauthorized = onUnauthorized;
	}

	async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		const token = await this.auth.getAuthToken();

		const config: RequestInit = {
			...options,
			headers: {
				"Content-Type": "application/json",
				...(token && { Authorization: `Bearer ${token}` }),
				...options.headers,
			},
		};

		const response = await fetch(`${this.baseURL}${endpoint}`, config);

		if (response.status === 401) {
			this.onUnauthorized();
			throw new Error("Session expired");
		}

		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new ApiError(
				errorMessageFromBody(body, response.statusText, response.status),
				response.status,
				body?.detail,
			);
		}

		if (
			response.status === 204 ||
			response.headers.get("content-length") === "0"
		) {
			return undefined as T;
		}
		return response.json();
	}

	get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
		let url = endpoint;
		if (params) {
			const searchParams = new URLSearchParams();
			for (const [key, value] of Object.entries(params)) {
				if (value !== undefined && value !== null) {
					searchParams.set(key, String(value));
				}
			}
			const qs = searchParams.toString();
			if (qs) url += `?${qs}`;
		}
		return this.request<T>(url, { method: "GET" });
	}

	post<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: "POST",
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	put<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: "PUT",
			body: JSON.stringify(data),
		});
	}

	patch<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: "PATCH",
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	delete<T>(endpoint: string): Promise<T> {
		return this.request<T>(endpoint, { method: "DELETE" });
	}

	async getBlob(endpoint: string): Promise<Blob> {
		const token = await this.auth.getAuthToken();
		const response = await fetch(`${this.baseURL}${endpoint}`, {
			method: "GET",
			headers: {
				...(token && { Authorization: `Bearer ${token}` }),
			},
		});
		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new Error(
				errorMessageFromBody(body, response.statusText, response.status),
			);
		}
		return response.blob();
	}

	async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
		const token = await this.auth.getAuthToken();
		const response = await fetch(`${this.baseURL}${endpoint}`, {
			method: "POST",
			headers: {
				...(token && { Authorization: `Bearer ${token}` }),
			},
			// Do NOT set Content-Type — browser sets multipart boundary automatically
			body: formData,
		});
		if (response.status === 401) {
			this.onUnauthorized();
			throw new Error("Session expired");
		}
		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new Error(
				errorMessageFromBody(body, response.statusText, response.status),
			);
		}
		if (
			response.status === 204 ||
			response.headers.get("content-length") === "0"
		) {
			return undefined as T;
		}
		return response.json();
	}

	async postWithSignal<T>(
		endpoint: string,
		data: unknown,
		signal?: AbortSignal,
	): Promise<T> {
		return this.request<T>(endpoint, {
			method: "POST",
			body: data ? JSON.stringify(data) : undefined,
			signal,
		});
	}
}

// ---------------------------------------------------------------------------
// Campaign API service — extends base with app-specific endpoints + 203 handling
// ---------------------------------------------------------------------------

class ApiService extends BaseApiService {
	static Endpoint = {
		ValidateInvite: "/auth/validate-invite",
		VerifyInviteToken: "/users/invite/verify",
		RegisterCandidate: "/candidates/register",
		GmapsGeocode: (address: string) =>
			`/campaigns/geocode?address=${encodeURIComponent(address)}` as const,
		Campaign: (campaignSlug: string) => `/campaigns/${campaignSlug}` as const,
		CampaignGoals: (campaignId: number) =>
			`/campaigns/${campaignId}/goals` as const,
		Session: "/auth/me",
		RefreshToken: "/auth/refresh",
		Logout: "/auth/logout",
		CiceroOfficials: (lat: number, lon: number, levels: string[]) =>
			`/cicero/officials?lat=${lat}&lon=${lon}&${levels.map((level) => `district_type=${level}`).join("&")}`,
		ActionGroups: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups` as const,
		ActionGroup: (campaignId: number, groupId: number) =>
			`/campaigns/${campaignId}/action-groups/${groupId}` as const,
		ActionGroupsPreview: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/preview` as const,
		ActionGroupMembers: (campaignId: number, groupId: number) =>
			`/campaigns/${campaignId}/action-groups/${groupId}/members` as const,
		ActionGroupMember: (
			campaignId: number,
			groupId: number,
			contactId: number,
		) =>
			`/campaigns/${campaignId}/action-groups/${groupId}/members/${contactId}` as const,
		ActionGroupMembersBulk: (campaignId: number, groupId: number) =>
			`/campaigns/${campaignId}/action-groups/${groupId}/members/bulk` as const,
		ContactGroups: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/groups` as const,
		ActionGroupAlerts: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/alerts` as const,
		ActionGroupAlertsCount: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/alerts/count` as const,
		ActionGroupAlertResolve: (campaignId: number, alertId: number) =>
			`/campaigns/${campaignId}/action-groups/alerts/${alertId}/resolve` as const,
		SuggestionsGenerate: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/suggestions/generate` as const,
		Suggestions: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/suggestions` as const,
		SuggestionAccept: (campaignId: number, sessionId: number, index: number) =>
			`/campaigns/${campaignId}/action-groups/suggestions/${sessionId}/${index}/accept` as const,
		SuggestionEdit: (campaignId: number, sessionId: number, index: number) =>
			`/campaigns/${campaignId}/action-groups/suggestions/${sessionId}/${index}/edit` as const,
		SuggestionDismiss: (campaignId: number, sessionId: number, index: number) =>
			`/campaigns/${campaignId}/action-groups/suggestions/${sessionId}/${index}/dismiss` as const,
		PreActionSuggestions: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/suggestions/pre-action` as const,
		GroupAuditLog: (campaignId: number, groupId: number) =>
			`/campaigns/${campaignId}/action-groups/${groupId}/audit-log` as const,
		RecoverContact: (campaignId: number, groupId: number, auditLogId: number) =>
			`/campaigns/${campaignId}/action-groups/${groupId}/recover/${auditLogId}` as const,
		ContactGroupHistory: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/group-history` as const,
		Guidance: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/guidance` as const,
		NLChatSessions: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/nl-chat/sessions` as const,
		NLChatSession: (campaignId: number, sessionId: number) =>
			`/campaigns/${campaignId}/action-groups/nl-chat/sessions/${sessionId}` as const,
		NLChatMessage: (campaignId: number, sessionId: number) =>
			`/campaigns/${campaignId}/action-groups/nl-chat/sessions/${sessionId}/message` as const,
		NLChatConfirm: (campaignId: number, sessionId: number) =>
			`/campaigns/${campaignId}/action-groups/nl-chat/sessions/${sessionId}/confirm` as const,
		VoterStats: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/voter-stats` as const,
		ContactSearchForGroup: (campaignId: number, groupId: number) =>
			`/campaigns/${campaignId}/action-groups/${groupId}/contacts/search` as const,
		Contacts: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts` as const,
		ContactsCount: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/count` as const,
		ContactDetail: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}` as const,
		ContactImportUpload: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/import/upload` as const,
		ContactImportMapping: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/contacts/import/${jobId}/mapping` as const,
		ContactImportStatus: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/contacts/import/${jobId}` as const,
		ContactImportStart: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/contacts/import/${jobId}/start` as const,
		ContactImportReport: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/contacts/import/${jobId}/report` as const,
		DonorImportUpload: (campaignId: number) =>
			`/campaigns/${campaignId}/fundraising/donors/import/upload` as const,
		DonorImportMapping: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/fundraising/donors/import/${jobId}/mapping` as const,
		DonorImportStart: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/fundraising/donors/import/${jobId}/start` as const,
		DonorImportStatus: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/fundraising/donors/import/${jobId}/status` as const,
		ContactRetryEnrichment: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/retry-enrichment` as const,
		ContactRetryAllEnrichments: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/retry-enrichment` as const,
		// People CRM endpoints
		People: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/people` as const,
		Pipeline: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/pipeline` as const,
		BulkStage: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/bulk-stage` as const,
		ContactStage: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/stage` as const,
		ContactRoles: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/roles` as const,
		Tags: (campaignId: number) => `/campaigns/${campaignId}/tags` as const,
		Tag: (campaignId: number, tagId: number) =>
			`/campaigns/${campaignId}/tags/${tagId}` as const,
		ContactTags: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/tags` as const,
		ContactTag: (campaignId: number, contactId: number, tagId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/tags/${tagId}` as const,
		FollowUps: (campaignId: number) =>
			`/campaigns/${campaignId}/follow-ups` as const,
		FollowUp: (campaignId: number, followUpId: number) =>
			`/campaigns/${campaignId}/follow-ups/${followUpId}` as const,
		ContactInteractions: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/interactions` as const,
		ContactTextInteractions: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/text-interactions` as const,
		CampaignHealth: (campaignId: number) =>
			`/campaigns/${campaignId}/health` as const,
		ContactIntelligence: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/intelligence` as const,
		// Strategy endpoints
		StrategyStatus: (campaignId: number) =>
			`/campaigns/${campaignId}/strategies/status` as const,
		Strategy: (campaignId: number) =>
			`/campaigns/${campaignId}/strategies/current` as const,
		// Strategy "me" endpoints (uses auth context, no campaign ID)
		StrategyMeStatus: "/strategies/me/status" as const,
		StrategyMe: "/strategies/me" as const,
		StrategyEditSection: (sectionId: string) =>
			`/strategies/me/sections/${sectionId}` as const,
		StrategyRegenerateSection: (sectionId: string) =>
			`/strategies/me/sections/${sectionId}/regenerate` as const,
		StrategySkipInterview: "/strategies/me/skip-interview" as const,
		StrategyRetry: "/strategies/me/retry" as const,
		StrategyRegenerate: "/strategies/me/regenerate" as const,
		StrategyStartOver: "/strategies/me/start-over" as const,
		// Strategy version history
		StrategyVersions: "/strategies/me/versions" as const,
		StrategyVersion: (version: number) =>
			`/strategies/me/versions/${version}` as const,
		StrategyRevert: (version: number) =>
			`/strategies/me/versions/${version}/revert` as const,
		// Strategy Corrections
		StrategyCorrections: "/strategies/v2/me/corrections" as const,
		StrategyCorrection: (id: number) =>
			`/strategies/v2/me/corrections/${id}` as const,
		StrategyCorrectionsSubmitAll:
			"/strategies/v2/me/corrections/submit-all" as const,
		// Strategic Roadmap
		StrategyRoadmap: "/strategies/v2/me/roadmap" as const,
		// Strategy Q&A conversations
		StrategyQAConversations: "/strategies/me/qa" as const,
		StrategyQAConversation: (conversationId: number) =>
			`/strategies/me/qa/${conversationId}` as const,
		StrategyQAMessage: (conversationId: number) =>
			`/strategies/me/qa/${conversationId}/message` as const,
		// Magic link (unauthenticated)
		MagicLinkValidate: "/strategies/magic-link/validate" as const,
		// Task Management
		Tasks: (campaignId: number) => `/campaigns/${campaignId}/tasks` as const,
		Task: (campaignId: number, taskId: number) =>
			`/campaigns/${campaignId}/tasks/${taskId}` as const,
		TasksCalendar: (campaignId: number) =>
			`/campaigns/${campaignId}/tasks/calendar` as const,
		TasksBulkStatus: (campaignId: number) =>
			`/campaigns/${campaignId}/tasks/bulk-status` as const,
		// Dashboard
		Dashboard: (campaignId: number) =>
			`/campaigns/${campaignId}/dashboard` as const,
		// Donations
		Donations: (campaignId: number) =>
			`/campaigns/${campaignId}/donations` as const,
		DonationDetail: (campaignId: number, donationId: number) =>
			`/campaigns/${campaignId}/donations/${donationId}` as const,
		DonationSummary: (campaignId: number) =>
			`/campaigns/${campaignId}/donations/summary` as const,
		// Daily Briefing
		Briefings: (campaignId: number) =>
			`/campaigns/${campaignId}/briefings` as const,
		BriefingToday: (campaignId: number) =>
			`/campaigns/${campaignId}/briefings/today` as const,
		BriefingCheckPending: (campaignId: number) =>
			`/campaigns/${campaignId}/briefings/check-pending` as const,
		BriefingGenerate: (campaignId: number) =>
			`/campaigns/${campaignId}/briefings/generate` as const,
		BriefingDetail: (campaignId: number, briefingId: number) =>
			`/campaigns/${campaignId}/briefings/${briefingId}` as const,
		BriefingStatus: (campaignId: number, briefingId: number) =>
			`/campaigns/${campaignId}/briefings/${briefingId}/status` as const,
		BriefingItem: (campaignId: number, briefingId: number, itemIndex: number) =>
			`/campaigns/${campaignId}/briefings/${briefingId}/items/${itemIndex}` as const,
		BriefingItemsBulk: (campaignId: number, briefingId: number) =>
			`/campaigns/${campaignId}/briefings/${briefingId}/items/bulk` as const,
		BriefingConversation: (campaignId: number, briefingId: number) =>
			`/campaigns/${campaignId}/briefings/${briefingId}/conversation` as const,
		BriefingStats: (campaignId: number) =>
			`/campaigns/${campaignId}/briefings/stats` as const,
		// Email Campaigns
		EmailCampaigns: (campaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns` as const,
		EmailCampaign: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}` as const,
		EmailCampaignDuplicate: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/duplicate` as const,
		EmailCampaignGenerateDraft: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/generate-draft` as const,
		EmailCampaignSend: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/send` as const,
		EmailCampaignSchedule: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/schedule` as const,
		EmailCampaignCancel: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/cancel` as const,
		EmailCampaignResetToDraft: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/reset-to-draft` as const,
		EmailCampaignSendPreview: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/send-preview` as const,
		EmailCampaignRecipientPreviews: (
			campaignId: number,
			emailCampaignId: number,
		) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/recipient-previews` as const,
		EmailCampaignPreview: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/preview` as const,
		EmailCampaignMetrics: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/metrics` as const,
		EmailCampaignRecipients: (campaignId: number, emailCampaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/${emailCampaignId}/recipients` as const,
		EmailCampaignAudiencePreview: (campaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/audience-preview` as const,
		EmailCampaignFromSuggestion: (campaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/from-suggestion` as const,
		EmailCampaignBriefingData: (campaignId: number) =>
			`/campaigns/${campaignId}/email/campaigns/briefing-data` as const,
		// Email Settings (candidate-facing)
		EmailSettings: (campaignId: number) =>
			`/campaigns/${campaignId}/email/settings` as const,
		// Email Templates
		EmailTemplates: (campaignId: number) =>
			`/campaigns/${campaignId}/email/templates` as const,
		EmailTemplatePreview: (campaignId: number, templateId: string) =>
			`/campaigns/${campaignId}/email/templates/${templateId}/preview` as const,
		// Team Management
		TeamUsers: (campaignId: number) =>
			`/campaigns/${campaignId}/users` as const,
		TeamUserRole: (campaignId: number, userId: number) =>
			`/campaigns/${campaignId}/users/${userId}/role` as const,
		TeamUserDeactivate: (campaignId: number, userId: number) =>
			`/campaigns/${campaignId}/users/${userId}/deactivate` as const,
		TeamUserReactivate: (campaignId: number, userId: number) =>
			`/campaigns/${campaignId}/users/${userId}/reactivate` as const,
		TeamInvite: (campaignId: number) =>
			`/campaigns/${campaignId}/users/invite` as const,
		TeamBulkDeactivate: (campaignId: number) =>
			`/campaigns/${campaignId}/users/bulk-deactivate` as const,
		TeamRoleHistory: (campaignId: number) =>
			`/campaigns/${campaignId}/users/role-history` as const,
		TeamInvites: (campaignId: number) =>
			`/campaigns/${campaignId}/invites` as const,
		TeamInviteResend: (campaignId: number, inviteId: number) =>
			`/campaigns/${campaignId}/invites/${inviteId}/resend` as const,
		TeamInviteRevoke: (campaignId: number, inviteId: number) =>
			`/campaigns/${campaignId}/invites/${inviteId}` as const,
		// NBA (Next Best Action)
		NbaActions: (campaignId: number) => `/campaigns/${campaignId}/nba` as const,
		// Xander AI Assistant
		XanderSession: (campaignId: number) =>
			`/campaigns/${campaignId}/xander/session` as const,
		XanderSessions: (campaignId: number) =>
			`/campaigns/${campaignId}/xander/sessions` as const,
		XanderSessionById: (campaignId: number, sessionId: number) =>
			`/campaigns/${campaignId}/xander/sessions/${sessionId}` as const,
		XanderActionConfirm: (
			campaignId: number,
			messageId: number,
			actionIndex: number,
		) =>
			`/campaigns/${campaignId}/xander/messages/${messageId}/actions/${actionIndex}/confirm` as const,
		XanderActionCancel: (
			campaignId: number,
			messageId: number,
			actionIndex: number,
		) =>
			`/campaigns/${campaignId}/xander/messages/${messageId}/actions/${actionIndex}/cancel` as const,
		XanderSuggestions: (campaignId: number) =>
			`/campaigns/${campaignId}/xander/suggestions` as const,
		XanderLightbulb: (campaignId: number) =>
			`/campaigns/${campaignId}/xander/lightbulb` as const,
		// Text Banking - TCPA Registration
		TcpaRegistration: (campaignId: number) =>
			`/campaigns/${campaignId}/text-banking/tcpa-registration` as const,
		// Text Banking - Shifts
		TextShifts: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/shifts` as const,
		TextShift: (campaignId: number, tcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/shifts/${shiftId}` as const,
		TextShiftActivate: (campaignId: number, tcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/shifts/${shiftId}/activate` as const,
		TextShiftComplete: (campaignId: number, tcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/shifts/${shiftId}/complete` as const,
		TextShiftJoin: (campaignId: number, tcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/shifts/${shiftId}/join` as const,
		TextShiftLeave: (campaignId: number, tcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/shifts/${shiftId}/leave` as const,
		TextShiftVolunteers: (campaignId: number, tcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/shifts/${shiftId}/volunteers` as const,
		// Text Banking - Campaigns
		TextCampaigns: (campaignId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns` as const,
		TextCampaign: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}` as const,
		TextCampaignLaunch: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/launch` as const,
		TextCampaignPause: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/pause` as const,
		TextCampaignCancel: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/cancel` as const,
		TextCampaignResume: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/resume` as const,
		TextCampaignDuplicate: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/duplicate` as const,
		TextCampaignAudiencePreview: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/audience-preview` as const,
		// Text Banking - P2P Queue
		TextP2PQueue: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/p2p-queue` as const,
		TextP2PSend: (campaignId: number, tcId: number, messageId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/p2p-send/${messageId}` as const,
		TextP2PGetMore: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/p2p-get-more` as const,
		// Text Banking - AI Script Generation
		TextAIGenerateScript: (campaignId: number) =>
			`/campaigns/${campaignId}/text-banking/ai/generate-script` as const,
		TextAISuggestAudience: (campaignId: number) =>
			`/campaigns/${campaignId}/text-banking/ai/suggest-audience` as const,
		// Text Banking - Volunteer Inbox (A2P reply management)
		VolunteerInbox: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/volunteer/inbox` as const,
		VolunteerThread: (campaignId: number, tcId: number, convId: string) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/volunteer/conversations/${convId}` as const,
		VolunteerReply: (campaignId: number, tcId: number, msgId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/volunteer/reply/${msgId}` as const,
		VolunteerResolve: (campaignId: number, tcId: number, convId: string) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/volunteer/resolve/${convId}` as const,
		// Text Banking - Metrics
		TextCampaignMetrics: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/metrics` as const,
		TextCampaignReplies: (campaignId: number, tcId: number) =>
			`/campaigns/${campaignId}/text-banking/campaigns/${tcId}/replies` as const,
		// Text Banking - Scripts
		TextScripts: (campaignId: number) =>
			`/campaigns/${campaignId}/text-banking/scripts` as const,
		TextScript: (campaignId: number, scriptId: number) =>
			`/campaigns/${campaignId}/text-banking/scripts/${scriptId}` as const,
		// Phone Banking - Campaigns
		PhoneCampaigns: (campaignId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns` as const,
		PhoneCampaign: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns/${pcId}` as const,
		PhoneCampaignLaunch: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns/${pcId}/launch` as const,
		PhoneCampaignPause: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns/${pcId}/pause` as const,
		PhoneCampaignResume: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns/${pcId}/resume` as const,
		PhoneCampaignCancel: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns/${pcId}/cancel` as const,
		// Phone Banking - Shifts
		PhoneShifts: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/shifts/${pcId}` as const,
		PhoneShiftJoin: (campaignId: number, pcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/phone-banking/shifts/${pcId}/${shiftId}/join` as const,
		PhoneShiftLeave: (campaignId: number, pcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/phone-banking/shifts/${pcId}/${shiftId}/leave` as const,
		PhoneShiftActivate: (campaignId: number, pcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/phone-banking/shifts/${pcId}/${shiftId}/activate` as const,
		PhoneShiftComplete: (campaignId: number, pcId: number, shiftId: number) =>
			`/campaigns/${campaignId}/phone-banking/shifts/${pcId}/${shiftId}/complete` as const,
		// Phone Banking - Scripts
		PhoneScriptGenerate: (campaignId: number) =>
			`/campaigns/${campaignId}/phone-banking/scripts/generate` as const,
		PhoneScript: (campaignId: number, scriptId: number) =>
			`/campaigns/${campaignId}/phone-banking/scripts/${scriptId}` as const,
		// Phone Banking - Calling Session
		PhoneCallToken: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/${pcId}/call/token` as const,
		PhoneCallNext: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/${pcId}/call/next` as const,
		PhoneCallStart: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/${pcId}/call/start` as const,
		PhoneCallDispose: (campaignId: number, pcId: number, attemptId: number) =>
			`/campaigns/${campaignId}/phone-banking/${pcId}/call/${attemptId}/dispose` as const,
		PhoneCallSkip: (campaignId: number, pcId: number, attemptId: number) =>
			`/campaigns/${campaignId}/phone-banking/${pcId}/call/${attemptId}/skip` as const,
		PhoneCallStats: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/${pcId}/call/stats` as const,
		// Phone Banking - Results/Metrics
		PhoneCampaignResults: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns/${pcId}/results` as const,
		PhoneCampaignResultsSummary: (campaignId: number, pcId: number) =>
			`/campaigns/${campaignId}/phone-banking/campaigns/${pcId}/results/summary` as const,
		// Phone Banking - Contact Phone History
		ContactPhoneHistory: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/phone-history` as const,
		// Public invite acceptance (no auth)
		AcceptInvite: "/invites/accept" as const,
		// Platform intro
		CompletePlatformIntro: "/users/me/complete-platform-intro" as const,
		// TOS acceptance
		AcceptTos: "/users/me/accept-tos" as const,
		TosStatus: "/users/me/tos-status" as const,
		// Call Time
		CallTimeList: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/list` as const,
		CallTimeSkippedList: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/list/skipped` as const,
		CallTimeListContext: (campaignId: number, listEntryId: number) =>
			`/campaigns/${campaignId}/call-time/list/${listEntryId}/context` as const,
		CallTimeListTalkingPoints: (campaignId: number, listEntryId: number) =>
			`/campaigns/${campaignId}/call-time/list/${listEntryId}/talking-points` as const,
		CallTimeEntries: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/entries` as const,
		CallTimeListStatus: (campaignId: number, listEntryId: number) =>
			`/campaigns/${campaignId}/call-time/list/${listEntryId}/status` as const,
		CallTimeStats: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/stats` as const,
		CallTimeGoal: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/results/goal` as const,
		// Call Time Dialer (PRD 32)
		CallTimeVoiceToken: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/token` as const,
		CallTimeDial: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/dial` as const,
		CallTimeHangup: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/hangup` as const,
		CallTimeTranscription: (campaignId: number, entryId: number) =>
			`/campaigns/${campaignId}/call-time/transcription/${entryId}` as const,
		CallTimeVerifyCallerId: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/verify-caller-id` as const,
		// Call Time — recipes (PRD-42 §5.4)
		CallTimeRecipes: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/recipes` as const,
		CallTimeRecipe: (campaignId: number, recipeId: number) =>
			`/campaigns/${campaignId}/call-time/recipes/${recipeId}` as const,
		CallTimeRecipeApply: (campaignId: number, recipeId: number) =>
			`/campaigns/${campaignId}/call-time/recipes/${recipeId}/apply` as const,
		CallTimeRecomputeScores: (campaignId: number) =>
			`/campaigns/${campaignId}/call-time/manage/list/recompute-scores` as const,
		// Contact Call Time History (S12)
		ContactCallHistory: (campaignId: number, contactId: number) =>
			`/campaigns/${campaignId}/contacts/${contactId}/call-time-history` as const,
		// Interview Follow-Up
		InterviewFollowUpQuestions: (campaignSlug: string) =>
			`/campaigns/${campaignSlug}/interview/follow-up-questions` as const,
		InterviewFollowUpAnswers: (campaignSlug: string) =>
			`/campaigns/${campaignSlug}/interview/follow-up-answers` as const,
		// Interview Attachments
		InterviewAttachmentUpload: (campaignSlug: string) =>
			`/campaigns/${campaignSlug}/interview/attachments/upload` as const,
		InterviewAttachmentList: (campaignSlug: string) =>
			`/campaigns/${campaignSlug}/interview/attachments` as const,
		InterviewAttachmentDelete: (campaignSlug: string, attachmentId: number) =>
			`/campaigns/${campaignSlug}/interview/attachments/${attachmentId}` as const,
		// Voter File (customer-facing)
		VoterFileRecords: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/records` as const,
		VoterFileCounties: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/counties` as const,
		VoterFileStatsAggregates: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/stats/aggregates` as const,
		VoterFileStatsParty: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/stats/party` as const,
		VoterFileStatsState: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/stats/state` as const,
		VoterFileStatsAge: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/stats/age` as const,
		// PRD-36: cross-campaign voter-file stats (scope from JWT)
		VoterFileStatsScopes: "/stats/scopes" as const,
		VoterFileStatsColumns: "/stats/columns" as const,
		VoterFileStatsQuery: "/stats/query" as const,
		VoterFileSavedFilters: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/saved-filters` as const,
		VoterFileSavedFilter: (campaignId: number, filterId: number) =>
			`/campaigns/${campaignId}/voter-file/saved-filters/${filterId}` as const,
		VoterFileContactsImport: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/contacts-import` as const,
		VoterFileContactsImportActive: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/contacts-import/active` as const,
		VoterFileContactsImportCancel: (campaignId: number, jobId: number) =>
			`/campaigns/${campaignId}/voter-file/contacts-import/${jobId}/cancel` as const,
		// Voter-file rows surfaced as read-only contacts in the contacts UI
		VoterFileContactsList: (campaignId: number) =>
			`/campaigns/${campaignId}/contacts/voter-file` as const,
		VoterFileContactDetail: (campaignId: number, l2VoterId: string) =>
			`/campaigns/${campaignId}/contacts/voter-file/${l2VoterId}` as const,
		VoterFileContactImport: (campaignId: number, l2VoterId: string) =>
			`/campaigns/${campaignId}/contacts/voter-file/${l2VoterId}/import` as const,
		// Voter-file AI query (natural-language classification + dispatch)
		VoterFileAIQuery: (campaignId: number) =>
			`/campaigns/${campaignId}/voter-file/ai-query` as const,
		// Voter-file action-group endpoints
		ActionGroupVoterFilePreview: (campaignId: number) =>
			`/campaigns/${campaignId}/action-groups/preview-voter-file` as const,
		ActionGroupVoterFileMembers: (campaignId: number, groupId: number) =>
			`/campaigns/${campaignId}/action-groups/${groupId}/voter-file-members` as const,
		// Prepaid Billing (PRD 45)
		BillingWallet: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/wallet` as const,
		BillingRates: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/rates` as const,
		BillingUsage: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/usage` as const,
		BillingUsageExport: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/usage/export.csv` as const,
		BillingTopUpIntent: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/top-up/intent` as const,
		BillingSetupIntent: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/payment-methods/setup-intent` as const,
		BillingPaymentMethods: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/payment-methods` as const,
		BillingPaymentMethodDefault: (campaignId: number, pmId: string) =>
			`/campaigns/${campaignId}/billing/payment-methods/${pmId}/default` as const,
		BillingPaymentMethodDetach: (campaignId: number, pmId: string) =>
			`/campaigns/${campaignId}/billing/payment-methods/${pmId}` as const,
		BillingEstimate: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/estimate` as const,
		BillingAutoReload: (campaignId: number) =>
			`/campaigns/${campaignId}/billing/auto-reload` as const,
		// Unified Outreach (PRD 46)
		OutreachAudienceCount: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/audience/count` as const,
		OutreachAudiences: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/audiences` as const,
		OutreachAudience: (campaignId: number, audienceId: number) =>
			`/campaigns/${campaignId}/outreach/audiences/${audienceId}` as const,
		OutreachAudienceParse: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/audience/parse` as const,
		OutreachAudienceRefine: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/audience/refine` as const,
		OutreachAudienceSuggestions: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/audience/suggestions` as const,
		OutreachGoalClarify: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/goal/clarify` as const,
		// Campaigns (OutreachGoal container) — PRD 53
		OutreachGoals: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/goals` as const,
		OutreachGoal: (campaignId: number, goalId: number) =>
			`/campaigns/${campaignId}/outreach/goals/${goalId}` as const,
		OutreachGoalSends: (campaignId: number, goalId: number) =>
			`/campaigns/${campaignId}/outreach/goals/${goalId}/sends` as const,
		OutreachCampaigns: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns` as const,
		OutreachCampaign: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}` as const,
		OutreachCampaignDraft: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/draft` as const,
		OutreachCampaignReview: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/review` as const,
		OutreachCampaignLaunch: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/launch` as const,
		OutreachCampaignDuplicate: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/duplicate` as const,
		OutreachCampaignTestSend: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/test-send` as const,
		OutreachCampaignTestSendEmail: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/test-send-email` as const,
		EmailImageUpload: (campaignId: number) =>
			`/campaigns/${campaignId}/email/images` as const,
		OutreachEmailPreview: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/email-preview` as const,
		OutreachCampaignCostToDate: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/cost-to-date` as const,
		// Smart Links (PRD 47)
		OutreachSmartLinkDomains: (campaignId: number) =>
			`/campaigns/${campaignId}/outreach/smart-link-domains` as const,
		OutreachSmartLinks: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/smart-links` as const,
		OutreachSmartLinksSync: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/smart-links/sync` as const,
		OutreachSmartLinksDomain: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/smart-links/domain` as const,
		OutreachSmartLinksMetrics: (campaignId: number, ocId: number) =>
			`/campaigns/${campaignId}/outreach/campaigns/${ocId}/smart-links/metrics` as const,
	} as const;

	override async request<T>(
		endpoint: string,
		options: RequestInit = {},
	): Promise<T> {
		const token = await authService.getAuthToken();

		const config: RequestInit = {
			...options,
			headers: {
				"Content-Type": "application/json",
				...(token && { Authorization: `Bearer ${token}` }),
				...options.headers,
			},
		};

		try {
			const response = await fetch(`${this.baseURL}${endpoint}`, config);

			if (response.status === 401) {
				authService.clearTokens();
				router.navigate({ to: "/login" });
				throw new Error("Session expired");
			} else if (response.status === 203) {
				router.navigate({ to: "/pending-approval" });
				const body = await response.json().catch(() => ({}));
				throw new Error(
					body?.detail ||
						body?.message ||
						"Your account is pending admin approval",
				);
			}

			if (!response.ok) {
				const body = await response.json().catch(() => ({}));
				// Throw ApiError (not plain Error) so structured `detail.code`
				// (e.g. 402 OUT_OF_CREDITS / INSUFFICIENT_CREDITS) survives for
				// getApiErrorCode() and the send-gate interstitials.
				throw new ApiError(
					errorMessageFromBody(body, response.statusText, response.status),
					response.status,
					body?.detail,
				);
			}

			if (
				response.status === 204 ||
				response.headers.get("content-length") === "0"
			) {
				return undefined as T;
			}

			return response.json();
		} catch (error) {
			console.error("API request failed:", error);
			throw error;
		}
	}

	/**
	 * Verify invite token
	 *
	 * @param token - The invite token to verify
	 *
	 * @returns An object containing the email, user_id, and message
	 * */
	async verifyInviteToken(token: string) {
		return this.request<any>(ApiService.Endpoint.VerifyInviteToken, {
			method: "GET",
			headers: { "X-Invite-Token": token },
		});
	}

	/**
	 * Register a new candidate using an invite token
	 *
	 * @param token - The invite token
	 * @param data - The registration data
	 *
	 * @returns An object containing a success message
	 */
	async registerCandidate(
		token: string,
		data: {
			email: string;
			password: string;
			username: string;
			confirm_password: string;
		},
	) {
		return this.request<{
			message: string;
			access_token?: string;
			refresh_token?: string;
			expires_in?: number;
		}>(ApiService.Endpoint.RegisterCandidate, {
			method: "POST",
			headers: {
				"X-Invite-Token": token,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		});
	}

	/**
	 * Get Google Maps geocode data for an address
	 *
	 * @param address - The address to geocode
	 *
	 * @returns An object containing the geocode results
	 */
	async getGmapsGeocode(address: string): Promise<
		| (GeocodeResponse["results"][number] & {
				completeAddress: string;
		  })
		| null
	> {
		const res = await this.get<GeocodeResponse>(
			ApiService.Endpoint.GmapsGeocode(address),
		);
		const addresses = res.results.map((data) => {
			const completeAddress = data.addressComponents
				.map((component) => component.shortText)
				.join(", ");
			return Object.assign(data, { completeAddress });
		});

		return addresses[0] ?? null;
	}

	/* Save campaign state */
	async saveCampaignState(data: {
		campaignSlug: string;
		value: Partial<CampaignGetResponse["campaign_data"]>;
		step: number;
		subStep?: number;
	}) {
		const response = await this.put(
			ApiService.Endpoint.Campaign(data.campaignSlug),
			{
				form_data: data.value,
				step: data.step,
				sub_step: data.subStep,
			},
		);
		return { success: true, data: response };
	}

	/** Get campaign data */
	async getCampaign(campaignSlug: string) {
		return this.get<CampaignGetResponse>(
			ApiService.Endpoint.Campaign(campaignSlug),
		);
	}

	/** Update campaign goal fields (fundraising_goal, budget_target, win_number) */
	async updateCampaignGoals(
		campaignId: number,
		data: CampaignGoalsPayload,
	): Promise<CampaignGetResponse> {
		return this.patch<CampaignGetResponse>(
			ApiService.Endpoint.CampaignGoals(campaignId),
			data,
		);
	}

	/** Get current user session */
	async getSession() {
		return this.get<SessionData | undefined>(ApiService.Endpoint.Session);
	}

	/** Mark the platform intro tour as completed */
	async completePlatformIntro() {
		return this.post<{ status: string }>(
			ApiService.Endpoint.CompletePlatformIntro,
		);
	}

	/** Accept Terms of Service and Privacy Policy */
	async acceptTos(body: {
		tos_version: string;
		privacy_policy_version: string;
		checkbox_confirmed: boolean;
	}) {
		return this.post<{ status: string; message: string }>(
			ApiService.Endpoint.AcceptTos,
			body,
		);
	}

	/** Get current TOS version and acceptance status */
	async getTosStatus() {
		return this.get<{
			tos_version: string;
			privacy_version: string;
			needs_acceptance: boolean;
		}>(ApiService.Endpoint.TosStatus);
	}

	/** Refresh authentication token */
	async refreshToken() {
		const refreshToken = authService.getRefreshToken();
		if (!refreshToken) {
			throw new Error("No refresh token available");
		}

		return this.request<AuthTokens>(ApiService.Endpoint.RefreshToken, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refresh_token: refreshToken }),
		});
	}

	async getCiceroOfficials(
		latitude: number,
		longitude: number,
		districtLevel: string[],
	) {
		const endpoint = ApiService.Endpoint.CiceroOfficials(
			latitude,
			longitude,
			districtLevel,
		);
		return this.get<CiceroOfficialsResponse>(endpoint);
	}

	/** Logout user and revoke server session */
	async logout() {
		return this.post<{ message: string; logged_out_sessions: number }>(
			ApiService.Endpoint.Logout,
		);
	}

	/** Get paginated list of action groups for a campaign */
	async getActionGroups(
		campaignId: number,
		params?: {
			page?: number;
			per_page?: number;
			group_type?: string;
			exclude_group_type?: string;
			source?: string;
		},
	) {
		const searchParams = new URLSearchParams();
		if (params?.page) searchParams.set("page", String(params.page));
		if (params?.per_page) searchParams.set("per_page", String(params.per_page));
		if (params?.group_type) searchParams.set("group_type", params.group_type);
		if (params?.exclude_group_type)
			searchParams.set("exclude_group_type", params.exclude_group_type);
		if (params?.source) searchParams.set("source", params.source);

		const query = searchParams.toString();
		const endpoint = `${ApiService.Endpoint.ActionGroups(campaignId)}${query ? `?${query}` : ""}`;
		return this.get<ActionGroupListResponse>(endpoint);
	}

	/** Get a single action group by ID */
	async getActionGroup(campaignId: number, groupId: number) {
		return this.get<ActionGroup>(
			ApiService.Endpoint.ActionGroup(campaignId, groupId),
		);
	}

	/** Create a new action group */
	async createActionGroup(campaignId: number, data: ActionGroupCreateRequest) {
		return this.post<ActionGroup>(
			ApiService.Endpoint.ActionGroups(campaignId),
			data,
		);
	}

	/** Update an existing action group */
	async updateActionGroup(
		campaignId: number,
		groupId: number,
		data: ActionGroupUpdateRequest,
	) {
		return this.put<ActionGroup>(
			ApiService.Endpoint.ActionGroup(campaignId, groupId),
			data,
		);
	}

	/** Delete an action group */
	async deleteActionGroup(campaignId: number, groupId: number) {
		return this.delete<{ message: string }>(
			ApiService.Endpoint.ActionGroup(campaignId, groupId),
		);
	}

	/** Preview filter rules to get matching contact count */
	async previewFilterRules(
		campaignId: number,
		filterRules: FilterRules,
	): Promise<FilterPreviewResponse> {
		return this.post<FilterPreviewResponse>(
			ApiService.Endpoint.ActionGroupsPreview(campaignId),
			{ filter_rules: filterRules },
		);
	}

	/** Preview a voter-file filter — indexed columns only, scope-locked. */
	async previewVoterFileFilterRules(
		campaignId: number,
		filterRules: FilterRules,
	): Promise<import("@/types/action-groups").VoterFileFilterPreviewResponse> {
		return this.post(
			ApiService.Endpoint.ActionGroupVoterFilePreview(campaignId),
			{ filter_rules: filterRules },
		);
	}

	/** Get paginated list of members for an action group */
	async getGroupMembers(
		campaignId: number,
		groupId: number,
		params?: {
			page?: number;
			per_page?: number;
			search?: string;
		},
	): Promise<GroupMemberListResponse> {
		const searchParams = new URLSearchParams();
		if (params?.page) searchParams.set("page", String(params.page));
		if (params?.per_page) searchParams.set("per_page", String(params.per_page));
		if (params?.search) searchParams.set("search", params.search);

		const query = searchParams.toString();
		const endpoint = `${ApiService.Endpoint.ActionGroupMembers(campaignId, groupId)}${query ? `?${query}` : ""}`;
		return this.get<GroupMemberListResponse>(endpoint);
	}

	/** Add members to an action group */
	async addGroupMembers(
		campaignId: number,
		groupId: number,
		contactIds: number[],
	): Promise<AddMembersResponse> {
		return this.post<AddMembersResponse>(
			ApiService.Endpoint.ActionGroupMembers(campaignId, groupId),
			{ contact_ids: contactIds },
		);
	}

	/** Remove a member from an action group */
	async removeGroupMember(
		campaignId: number,
		groupId: number,
		contactId: number,
	): Promise<{ message: string }> {
		return this.delete<{ message: string }>(
			ApiService.Endpoint.ActionGroupMember(campaignId, groupId, contactId),
		);
	}

	/** Bulk add members to an action group */
	async bulkAddGroupMembers(
		campaignId: number,
		groupId: number,
		contactIds: number[],
	): Promise<AddMembersResponse> {
		return this.post<AddMembersResponse>(
			ApiService.Endpoint.ActionGroupMembersBulk(campaignId, groupId),
			{ contact_ids: contactIds },
		);
	}

	/** Get groups a contact belongs to */
	async getContactGroups(
		campaignId: number,
		contactId: number,
	): Promise<ContactGroupsResponse> {
		return this.get<ContactGroupsResponse>(
			ApiService.Endpoint.ContactGroups(campaignId, contactId),
		);
	}

	/** Get pending auto-removal alerts for a campaign */
	async getAlerts(campaignId: number): Promise<PendingAlertListResponse> {
		return this.get<PendingAlertListResponse>(
			ApiService.Endpoint.ActionGroupAlerts(campaignId),
		);
	}

	/** Get count of pending auto-removal alerts */
	async getAlertCount(campaignId: number): Promise<PendingAlertCountResponse> {
		return this.get<PendingAlertCountResponse>(
			ApiService.Endpoint.ActionGroupAlertsCount(campaignId),
		);
	}

	/** Resolve an auto-removal alert */
	async resolveAlert(
		campaignId: number,
		alertId: number,
		action: AlertAction,
	): Promise<ResolvedAlertResponse> {
		return this.post<ResolvedAlertResponse>(
			ApiService.Endpoint.ActionGroupAlertResolve(campaignId, alertId),
			{ action },
		);
	}

	/** Generate AI group suggestions for a campaign */
	async generateSuggestions(
		campaignId: number,
	): Promise<SuggestionSessionResponse> {
		return this.post<SuggestionSessionResponse>(
			ApiService.Endpoint.SuggestionsGenerate(campaignId),
		);
	}

	/** Get the current AI suggestion session for a campaign */
	async getSuggestions(campaignId: number): Promise<SuggestionSessionResponse> {
		return this.get<SuggestionSessionResponse>(
			ApiService.Endpoint.Suggestions(campaignId),
		);
	}

	/** Accept an AI suggestion and create the action group */
	async acceptSuggestion(
		campaignId: number,
		sessionId: number,
		index: number,
	): Promise<ActionGroup> {
		return this.post<ActionGroup>(
			ApiService.Endpoint.SuggestionAccept(campaignId, sessionId, index),
		);
	}

	/** Edit an AI suggestion and create the action group with overrides */
	async editSuggestion(
		campaignId: number,
		sessionId: number,
		index: number,
		data: AcceptSuggestionRequest,
	): Promise<ActionGroup> {
		return this.post<ActionGroup>(
			ApiService.Endpoint.SuggestionEdit(campaignId, sessionId, index),
			data,
		);
	}

	/** Dismiss an AI suggestion */
	async dismissSuggestion(
		campaignId: number,
		sessionId: number,
		index: number,
	): Promise<{ message: string }> {
		return this.post<{ message: string }>(
			ApiService.Endpoint.SuggestionDismiss(campaignId, sessionId, index),
		);
	}

	/** Get pre-action group suggestions for outreach */
	async getPreActionSuggestions(
		campaignId: number,
		actionType: "text_campaign" | "phone_campaign",
		context?: Record<string, unknown>,
	): Promise<PreActionSuggestionResponse> {
		return this.post<PreActionSuggestionResponse>(
			ApiService.Endpoint.PreActionSuggestions(campaignId),
			{ action_type: actionType, context },
		);
	}

	/** Start a new NL group chat session */
	async nlGroupStart(campaignId: number): Promise<NLGroupStartResponse> {
		return this.post<NLGroupStartResponse>(
			ApiService.Endpoint.NLChatSessions(campaignId),
		);
	}

	/** Get NL group chat session state */
	async nlGroupGetSession(
		campaignId: number,
		sessionId: number,
	): Promise<NLGroupSessionResponse> {
		return this.get<NLGroupSessionResponse>(
			ApiService.Endpoint.NLChatSession(campaignId, sessionId),
		);
	}

	/** Send a message in an NL group chat session */
	async nlGroupMessage(
		campaignId: number,
		sessionId: number,
		message: string,
	): Promise<NLGroupMessageResponse> {
		return this.post<NLGroupMessageResponse>(
			ApiService.Endpoint.NLChatMessage(campaignId, sessionId),
			{ message },
		);
	}

	/** Confirm proposed NL groups and create action groups */
	async nlGroupConfirm(
		campaignId: number,
		sessionId: number,
		selectedIndices?: number[],
	): Promise<NLGroupConfirmResponse> {
		return this.post<NLGroupConfirmResponse>(
			ApiService.Endpoint.NLChatConfirm(campaignId, sessionId),
			{ selected_indices: selectedIndices ?? null },
		);
	}

	// ---------------------------------------------------------------------------
	// Voter File (customer-facing)
	// ---------------------------------------------------------------------------

	async getVoterFileRecords(
		campaignId: number,
		params: Record<string, string | number | boolean | undefined>,
	): Promise<import("@/types/voter-file").VoterFileRecordListResponse> {
		const qs = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
		}
		const q = qs.toString();
		return this.get(
			`${ApiService.Endpoint.VoterFileRecords(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/**
	 * List counties (FIPS + name) for the campaign's state. Powers the
	 * state-limited county combobox on the voter-file page. ``state``
	 * defaults to the campaign's locked voter_file_state server-side.
	 */
	async getVoterFileCounties(
		campaignId: number,
		state?: string | null,
	): Promise<import("@/types/voter-file").VoterFileCountyListResponse> {
		const s = (state ?? "").trim().toUpperCase();
		const q = s ? `?state=${encodeURIComponent(s)}` : "";
		return this.get(`${ApiService.Endpoint.VoterFileCounties(campaignId)}${q}`);
	}

	async getVoterFileContacts(
		campaignId: number,
		params?: Record<string, string | number | boolean | undefined>,
	): Promise<import("@/types/voter-file").VoterFileContactListResponse> {
		const qs = new URLSearchParams();
		if (params) {
			for (const [k, v] of Object.entries(params)) {
				if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
			}
		}
		const q = qs.toString();
		return this.get(
			`${ApiService.Endpoint.VoterFileContactsList(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	async getVoterFileContactDetail(
		campaignId: number,
		l2VoterId: string,
	): Promise<import("@/types/voter-file").VoterFileContactDetail> {
		return this.get(
			ApiService.Endpoint.VoterFileContactDetail(campaignId, l2VoterId),
		);
	}

	async importVoterFileContact(
		campaignId: number,
		l2VoterId: string,
	): Promise<{
		contact_id: number | null;
		voter_file_record_id: number;
		status: "created" | "already_exists";
	}> {
		return this.post(
			ApiService.Endpoint.VoterFileContactImport(campaignId, l2VoterId),
			{},
		);
	}

	private _buildFilterQs(
		params?: Record<string, string | number | boolean | undefined>,
	): string {
		const qs = new URLSearchParams();
		if (params) {
			for (const [k, v] of Object.entries(params)) {
				if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
			}
		}
		const q = qs.toString();
		return q ? `?${q}` : "";
	}

	async getVoterFileAggregates(
		campaignId: number,
		params?: Record<string, string | number | boolean | undefined>,
	): Promise<import("@/types/voter-file").VoterFileAggregates> {
		return this.get(
			`${ApiService.Endpoint.VoterFileStatsAggregates(campaignId)}${this._buildFilterQs(params)}`,
		);
	}

	async getVoterFilePartyBreakdown(
		campaignId: number,
		params?: Record<string, string | number | boolean | undefined>,
	): Promise<import("@/types/voter-file").VoterFilePartyBreakdown> {
		return this.get(
			`${ApiService.Endpoint.VoterFileStatsParty(campaignId)}${this._buildFilterQs(params)}`,
		);
	}

	async getVoterFileStateBreakdown(
		campaignId: number,
		params?: Record<string, string | number | boolean | undefined>,
	): Promise<import("@/types/voter-file").VoterFileStateBreakdown> {
		return this.get(
			`${ApiService.Endpoint.VoterFileStatsState(campaignId)}${this._buildFilterQs(params)}`,
		);
	}

	async getVoterFileAgeBreakdown(
		campaignId: number,
		params?: Record<string, string | number | boolean | undefined>,
	): Promise<import("@/types/voter-file").VoterFileAgeBreakdown> {
		return this.get(
			`${ApiService.Endpoint.VoterFileStatsAge(campaignId)}${this._buildFilterQs(params)}`,
		);
	}

	// PRD-36: cross-campaign VF stats — scope is derived server-side from JWT.
	async getVoterFileStatsScopes(): Promise<
		import("@/types/voterfile-stats").ResolvedScope
	> {
		return this.get(ApiService.Endpoint.VoterFileStatsScopes);
	}

	async getVoterFileStatsColumns(): Promise<
		import("@/types/voterfile-stats").ColumnsCatalog
	> {
		return this.get(ApiService.Endpoint.VoterFileStatsColumns);
	}

	async runVoterFileStatsQuery(
		body: import("@/types/voterfile-stats").QueryRequest,
	): Promise<import("@/types/voterfile-stats").QueryResult> {
		return this.post(ApiService.Endpoint.VoterFileStatsQuery, body);
	}

	async runVoterFileAIQuery(
		campaignId: number,
		body: import("@/types/voterfile-ai-query").VoterFileAIQueryRequest,
	): Promise<import("@/types/voterfile-ai-query").VoterFileAIQueryResponse> {
		return this.post(ApiService.Endpoint.VoterFileAIQuery(campaignId), body);
	}

	async getVoterFileSavedFilters(
		campaignId: number,
	): Promise<import("@/types/voter-file").SavedVoterFilter[]> {
		return this.get(ApiService.Endpoint.VoterFileSavedFilters(campaignId));
	}

	async createVoterFileSavedFilter(
		campaignId: number,
		data: import("@/types/voter-file").SavedVoterFilterCreate,
	): Promise<import("@/types/voter-file").SavedVoterFilter> {
		return this.post(
			ApiService.Endpoint.VoterFileSavedFilters(campaignId),
			data,
		);
	}

	async deleteVoterFileSavedFilter(
		campaignId: number,
		filterId: number,
	): Promise<void> {
		return this.delete(
			ApiService.Endpoint.VoterFileSavedFilter(campaignId, filterId),
		);
	}

	async startVoterFileContactsImport(
		campaignId: number,
		data: import("@/types/voter-file-import").StartVoterFileImportRequest,
	): Promise<import("@/types/voter-file-import").VoterFileImportJob> {
		return this.post(
			ApiService.Endpoint.VoterFileContactsImport(campaignId),
			data,
		);
	}

	async listVoterFileContactsImports(
		campaignId: number,
		page = 1,
		pageSize = 25,
	): Promise<
		import("@/types/voter-file-import").VoterFileImportJobListResponse
	> {
		const qs = new URLSearchParams({
			page: String(page),
			page_size: String(pageSize),
		});
		return this.get(
			`${ApiService.Endpoint.VoterFileContactsImport(campaignId)}?${qs}`,
		);
	}

	async getActiveVoterFileContactsImport(
		campaignId: number,
	): Promise<import("@/types/voter-file-import").VoterFileImportJob | null> {
		return this.get(
			ApiService.Endpoint.VoterFileContactsImportActive(campaignId),
		);
	}

	async cancelVoterFileContactsImport(
		campaignId: number,
		jobId: number,
	): Promise<import("@/types/voter-file-import").VoterFileImportJob> {
		return this.post(
			ApiService.Endpoint.VoterFileContactsImportCancel(campaignId, jobId),
			{},
		);
	}

	/** Get voter statistics for a campaign */
	async getVoterStats(campaignId: number): Promise<VoterStats> {
		return this.get<VoterStats>(ApiService.Endpoint.VoterStats(campaignId));
	}

	/** Get audit log for a group */
	async getGroupAuditLog(
		campaignId: number,
		groupId: number,
		page = 1,
		perPage = 20,
		actionFilter?: string,
	): Promise<AuditLogListResponse> {
		const params = new URLSearchParams({
			page: String(page),
			per_page: String(perPage),
		});
		if (actionFilter) params.set("action", actionFilter);
		return this.get<AuditLogListResponse>(
			`${ApiService.Endpoint.GroupAuditLog(campaignId, groupId)}?${params}`,
		);
	}

	/** Recover a removed contact */
	async recoverContact(
		campaignId: number,
		groupId: number,
		auditLogId: number,
	): Promise<AuditLogEntry> {
		return this.post<AuditLogEntry>(
			ApiService.Endpoint.RecoverContact(campaignId, groupId, auditLogId),
		);
	}

	/** Get contact's group history */
	async getContactGroupHistory(
		campaignId: number,
		contactId: number,
	): Promise<ContactGroupHistoryResponse> {
		return this.get<ContactGroupHistoryResponse>(
			ApiService.Endpoint.ContactGroupHistory(campaignId, contactId),
		);
	}

	/** Get group guidance */
	async getGuidance(campaignId: number): Promise<GuidanceResponse> {
		return this.get<GuidanceResponse>(ApiService.Endpoint.Guidance(campaignId));
	}

	/** Search contacts for adding to a group (excludes existing members) */
	async searchContactsForGroup(
		campaignId: number,
		groupId: number,
		query: string,
		limit = 20,
	): Promise<ContactSearchResponse> {
		const params = new URLSearchParams({
			q: query,
			limit: String(limit),
		});
		return this.get<ContactSearchResponse>(
			`${ApiService.Endpoint.ContactSearchForGroup(campaignId, groupId)}?${params}`,
		);
	}

	/**
	 * Get paginated list of REAL contacts for a campaign (the ``contacts``
	 * table — donors, volunteers, CSV imports, voter-file imports — i.e.
	 * everything the campaign actually has on file). Accepts the same
	 * filter+sort query shape as ``/voter-file/records`` so the contacts
	 * page can reuse the shared filter-chip-strip UX. Filters the backend
	 * doesn't understand (propensity, voter-file-only enrichment) are
	 * silently ignored — the matching column cells render as ``—`` on the
	 * client.
	 */
	async getContacts(
		campaignId: number,
		params?: Record<string, string | number | boolean | undefined>,
	): Promise<ContactListResponse> {
		const qs = new URLSearchParams();
		if (params) {
			for (const [k, v] of Object.entries(params)) {
				if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
			}
		}
		const q = qs.toString();
		const endpoint = `${ApiService.Endpoint.Contacts(campaignId)}${q ? `?${q}` : ""}`;
		return this.get<ContactListResponse>(endpoint);
	}

	/**
	 * Total contacts matching the given filters/search, fetched separately
	 * from the row page so the two can run in parallel (the count is the
	 * slow part for large campaigns). Only filter + search params are
	 * meaningful here — page/sort do not affect the total.
	 */
	async getContactsCount(
		campaignId: number,
		params?: Record<string, string | number | boolean | undefined>,
	): Promise<ContactCountResponse> {
		const qs = new URLSearchParams();
		if (params) {
			for (const [k, v] of Object.entries(params)) {
				if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
			}
		}
		const q = qs.toString();
		const endpoint = `${ApiService.Endpoint.ContactsCount(campaignId)}${q ? `?${q}` : ""}`;
		return this.get<ContactCountResponse>(endpoint);
	}

	/** Get a single contact by ID */
	async getContact(campaignId: number, contactId: number): Promise<Contact> {
		return this.get<Contact>(
			ApiService.Endpoint.ContactDetail(campaignId, contactId),
		);
	}

	/** Get call time history for a specific contact */
	async getContactCallHistory(
		campaignId: number,
		contactId: number,
		limit = 20,
		offset = 0,
	) {
		return this.get<ContactCallHistoryResponse>(
			`/campaigns/${campaignId}/contacts/${contactId}/call-time-history?limit=${limit}&offset=${offset}`,
		);
	}

	/** Create a new contact */
	async createContact(
		campaignId: number,
		data: ContactCreate,
	): Promise<Contact> {
		return this.post<Contact>(ApiService.Endpoint.Contacts(campaignId), data);
	}

	/** Update an existing contact */
	async updateContact(
		campaignId: number,
		contactId: number,
		data: Partial<ContactCreate>,
	): Promise<Contact> {
		return this.put<Contact>(
			ApiService.Endpoint.ContactDetail(campaignId, contactId),
			data,
		);
	}

	/** Delete a contact permanently */
	async deleteContact(campaignId: number, contactId: number): Promise<void> {
		return this.delete(
			ApiService.Endpoint.ContactDetail(campaignId, contactId),
		);
	}

	/** Upload a CSV file for contact import */
	async uploadCSV(campaignId: number, file: File): Promise<CSVUploadResponse> {
		const token = await authService.getAuthToken();
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetch(
			`${this.baseURL}${ApiService.Endpoint.ContactImportUpload(campaignId)}`,
			{
				method: "POST",
				headers: {
					...(token && { Authorization: `Bearer ${token}` }),
				},
				body: formData,
			},
		);

		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new Error(
				errorMessageFromBody(body, response.statusText, response.status),
			);
		}

		return response.json();
	}

	/** Confirm column mapping for a CSV import */
	async confirmMapping(
		campaignId: number,
		jobId: number,
		mappings: Record<string, string | null>,
	): Promise<ColumnMappingResponse> {
		return this.post<ColumnMappingResponse>(
			ApiService.Endpoint.ContactImportMapping(campaignId, jobId),
			{ mappings },
		);
	}

	/** Start processing a CSV import */
	async startImport(
		campaignId: number,
		jobId: number,
	): Promise<ImportStartResponse> {
		return this.post<ImportStartResponse>(
			ApiService.Endpoint.ContactImportStart(campaignId, jobId),
		);
	}

	/** Get CSV import job status */
	async getImportJobStatus(
		campaignId: number,
		jobId: number,
	): Promise<CSVImportJob> {
		return this.get<CSVImportJob>(
			ApiService.Endpoint.ContactImportStatus(campaignId, jobId),
		);
	}

	/** Retry enrichment for a single contact */
	async retryEnrichment(
		campaignId: number,
		contactId: number,
	): Promise<{ contact_id: number; task_id: string; status: string }> {
		return this.post(
			ApiService.Endpoint.ContactRetryEnrichment(campaignId, contactId),
		);
	}

	/** Retry enrichment for all error contacts in a campaign */
	async retryAllEnrichments(campaignId: number): Promise<{
		total: number;
		matched: number;
		not_found: number;
		errored: number;
	}> {
		return this.post(
			ApiService.Endpoint.ContactRetryAllEnrichments(campaignId),
		);
	}

	/** Submit a support report for a failed import */
	async submitImportReport(
		campaignId: number,
		jobId: number,
	): Promise<{ message: string }> {
		return this.post(
			ApiService.Endpoint.ContactImportReport(campaignId, jobId),
		);
	}

	// -----------------------------------------------------------------------
	// Donor CSV Import
	// -----------------------------------------------------------------------

	/** Upload a CSV file for donor import */
	async uploadDonorCSV(
		campaignId: number,
		file: File,
	): Promise<CSVUploadResponse> {
		const token = await authService.getAuthToken();
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetch(
			`${this.baseURL}${ApiService.Endpoint.DonorImportUpload(campaignId)}`,
			{
				method: "POST",
				headers: {
					...(token && { Authorization: `Bearer ${token}` }),
				},
				body: formData,
			},
		);

		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new Error(
				errorMessageFromBody(body, response.statusText, response.status),
			);
		}

		return response.json();
	}

	/** Confirm column mapping for a donor CSV import */
	async confirmDonorMapping(
		campaignId: number,
		jobId: number,
		mappings: Record<string, string | null>,
	): Promise<ColumnMappingResponse> {
		return this.post<ColumnMappingResponse>(
			ApiService.Endpoint.DonorImportMapping(campaignId, jobId),
			{ mappings },
		);
	}

	/** Start processing a donor CSV import */
	async startDonorImport(
		campaignId: number,
		jobId: number,
	): Promise<ImportStartResponse> {
		return this.post<ImportStartResponse>(
			ApiService.Endpoint.DonorImportStart(campaignId, jobId),
		);
	}

	/** Get donor CSV import job status */
	async getDonorImportJobStatus(
		campaignId: number,
		jobId: number,
	): Promise<CSVImportJob> {
		return this.get<CSVImportJob>(
			ApiService.Endpoint.DonorImportStatus(campaignId, jobId),
		);
	}

	// -----------------------------------------------------------------------
	// People CRM
	// -----------------------------------------------------------------------

	/** Enhanced people list with stage/role/tag filters */
	async getPeople(
		campaignId: number,
		params?: {
			page?: number;
			per_page?: number;
			search?: string;
			stage?: string;
			role?: string;
			tag_id?: number;
			group_id?: number;
		},
	): Promise<PeopleListResponse> {
		const sp = new URLSearchParams();
		if (params?.page) sp.set("page", String(params.page));
		if (params?.per_page) sp.set("per_page", String(params.per_page));
		if (params?.search) sp.set("search", params.search);
		if (params?.stage) sp.set("stage", params.stage);
		if (params?.role) sp.set("role", params.role);
		if (params?.tag_id) sp.set("tag_id", String(params.tag_id));
		if (params?.group_id) sp.set("group_id", String(params.group_id));
		const q = sp.toString();
		return this.get<PeopleListResponse>(
			`${ApiService.Endpoint.People(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/** Update a contact's engagement stage */
	async updateContactStage(
		campaignId: number,
		contactId: number,
		stage: string,
	): Promise<Contact> {
		return this.patch<Contact>(
			ApiService.Endpoint.ContactStage(campaignId, contactId),
			{ stage },
		);
	}

	/** Bulk update stages */
	async bulkUpdateStage(
		campaignId: number,
		contactIds: number[],
		stage: string,
	): Promise<{ updated: number }> {
		return this.patch<{ updated: number }>(
			ApiService.Endpoint.BulkStage(campaignId),
			{ contact_ids: contactIds, stage },
		);
	}

	/** Update a contact's roles */
	async updateContactRoles(
		campaignId: number,
		contactId: number,
		roles: string[],
	): Promise<Contact> {
		return this.patch<Contact>(
			ApiService.Endpoint.ContactRoles(campaignId, contactId),
			{ roles },
		);
	}

	/** Get pipeline data (kanban) */
	async getPipeline(campaignId: number): Promise<PipelineResponse> {
		return this.get<PipelineResponse>(ApiService.Endpoint.Pipeline(campaignId));
	}

	/** List campaign tags */
	async getTags(campaignId: number): Promise<{ tags: ContactTag[] }> {
		return this.get<{ tags: ContactTag[] }>(
			ApiService.Endpoint.Tags(campaignId),
		);
	}

	/** Create a tag */
	async createTag(
		campaignId: number,
		data: { name: string; color?: string },
	): Promise<ContactTag> {
		return this.post<ContactTag>(ApiService.Endpoint.Tags(campaignId), data);
	}

	/** Delete a tag */
	async deleteTag(campaignId: number, tagId: number): Promise<void> {
		return this.delete(ApiService.Endpoint.Tag(campaignId, tagId));
	}

	/** Add tag to contact */
	async addTagToContact(
		campaignId: number,
		contactId: number,
		tagId: number,
	): Promise<void> {
		return this.post(ApiService.Endpoint.ContactTags(campaignId, contactId), {
			tag_id: tagId,
		});
	}

	/** Remove tag from contact */
	async removeTagFromContact(
		campaignId: number,
		contactId: number,
		tagId: number,
	): Promise<void> {
		return this.delete(
			ApiService.Endpoint.ContactTag(campaignId, contactId, tagId),
		);
	}

	/** List follow-ups */
	async getFollowUps(
		campaignId: number,
		params?: {
			filter?: string;
			page?: number;
			per_page?: number;
			contact_id?: number;
		},
	): Promise<FollowUpListResponse> {
		const sp = new URLSearchParams();
		if (params?.filter) sp.set("filter", params.filter);
		if (params?.page) sp.set("page", String(params.page));
		if (params?.per_page) sp.set("per_page", String(params.per_page));
		if (params?.contact_id) sp.set("contact_id", String(params.contact_id));
		const q = sp.toString();
		return this.get<FollowUpListResponse>(
			`${ApiService.Endpoint.FollowUps(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/** Create a follow-up */
	async createFollowUp(
		campaignId: number,
		data: {
			contact_id: number;
			type: string;
			description?: string;
			due_date: string;
			priority?: string;
		},
	): Promise<FollowUp> {
		return this.post<FollowUp>(ApiService.Endpoint.FollowUps(campaignId), data);
	}

	/** Update follow-up (complete/snooze/dismiss) */
	async updateFollowUp(
		campaignId: number,
		followUpId: number,
		data: { status?: string; snoozed_until?: string },
	): Promise<FollowUp> {
		return this.patch<FollowUp>(
			ApiService.Endpoint.FollowUp(campaignId, followUpId),
			data,
		);
	}

	/** Log a contact interaction */
	async logInteraction(
		campaignId: number,
		contactId: number,
		data: {
			type: string;
			description?: string;
			metadata?: Record<string, unknown>;
			interaction_date?: string;
		},
	): Promise<ContactInteraction> {
		return this.post<ContactInteraction>(
			ApiService.Endpoint.ContactInteractions(campaignId, contactId),
			data,
		);
	}

	/** Get contact interactions */
	async getContactInteractions(
		campaignId: number,
		contactId: number,
		page = 1,
		perPage = 20,
	): Promise<{ interactions: ContactInteraction[]; total: number }> {
		const sp = new URLSearchParams({
			page: String(page),
			per_page: String(perPage),
		});
		return this.get(
			`${ApiService.Endpoint.ContactInteractions(campaignId, contactId)}?${sp}`,
		);
	}

	/** Get donor interactions */
	async getDonorInteractions(campaignId: number, donorId: number) {
		return this.get<{
			interactions: Array<{
				id: number;
				type: string;
				description: string | null;
				interaction_date: string;
				created_at: string;
			}>;
			total: number;
		}>(`/campaigns/${campaignId}/fundraising/donors/${donorId}/interactions`);
	}

	/** Log a donor interaction */
	async logDonorInteraction(
		campaignId: number,
		donorId: number,
		data: { type: string; description?: string; interaction_date?: string },
	) {
		return this.post<{
			id: number;
			type: string;
			description: string | null;
			interaction_date: string;
			created_at: string;
		}>(
			`/campaigns/${campaignId}/fundraising/donors/${donorId}/interactions`,
			data,
		);
	}

	/** Get donor call history */
	async getDonorCallHistory(campaignId: number, donorId: number) {
		return this.get<{
			entries: Array<{
				id: number;
				disposition: string;
				pledge_amount: number | null;
				notes: string | null;
				duration_seconds: number | null;
				purpose: string | null;
				caller_name: string | null;
				called_at: string;
			}>;
			total: number;
		}>(`/campaigns/${campaignId}/fundraising/donors/${donorId}/call-history`);
	}

	/** Get text message interaction history for a contact */
	async getContactTextInteractions(
		campaignId: number,
		contactId: number,
	): Promise<TextInteractionResponse[]> {
		return this.get<TextInteractionResponse[]>(
			ApiService.Endpoint.ContactTextInteractions(campaignId, contactId),
		);
	}

	/** Get campaign health metrics */
	async getCampaignHealth(campaignId: number): Promise<CampaignHealth> {
		return this.get<CampaignHealth>(
			ApiService.Endpoint.CampaignHealth(campaignId),
		);
	}

	/** Get AI-powered contact intelligence */
	async getContactIntelligence(
		campaignId: number,
		contactId: number,
	): Promise<ContactIntelligence> {
		return this.get<ContactIntelligence>(
			ApiService.Endpoint.ContactIntelligence(campaignId, contactId),
		);
	}

	// -----------------------------------------------------------------------
	// Strategy
	// -----------------------------------------------------------------------

	/** Get strategy generation status for a campaign */
	async getStrategyStatus(campaignId: number): Promise<StrategyStatusResponse> {
		return this.get<StrategyStatusResponse>(
			ApiService.Endpoint.StrategyStatus(campaignId),
		);
	}

	/** Get the current strategy for a campaign */
	async getStrategy(campaignId: number): Promise<CampaignStrategy> {
		return this.get<CampaignStrategy>(ApiService.Endpoint.Strategy(campaignId));
	}

	/** TEMPORARY BYPASS: Skip interview and trigger strategy generation. Remove when interview system is restored. */
	async skipInterviewAndGenerate(): Promise<{
		status: string;
		message: string;
	}> {
		return this.post<{ status: string; message: string }>(
			ApiService.Endpoint.StrategySkipInterview,
		);
	}

	/** Retry failed strategy generation */
	async retryStrategyGeneration(): Promise<{
		status: string;
		message: string;
	}> {
		return this.post<{ status: string; message: string }>(
			ApiService.Endpoint.StrategyRetry,
		);
	}

	/** Get strategy status for the current user's campaign */
	async getMyStrategyStatus(): Promise<StrategyStatusResponse> {
		return this.get<StrategyStatusResponse>(
			ApiService.Endpoint.StrategyMeStatus,
		);
	}

	/** Get strategy for the current user's campaign */
	async getMyStrategy(): Promise<Record<string, unknown>> {
		return this.get<Record<string, unknown>>(ApiService.Endpoint.StrategyMe);
	}

	/** Get strategic roadmap (boulders, rocks, gravel) */
	async getStrategyRoadmap(): Promise<Record<string, unknown>> {
		return this.get<Record<string, unknown>>(
			ApiService.Endpoint.StrategyRoadmap,
		);
	}

	/** List pending corrections for current strategy */
	async listCorrections(): Promise<{ corrections: Correction[] }> {
		return this.get<{ corrections: Correction[] }>(
			ApiService.Endpoint.StrategyCorrections,
		);
	}

	/** Add a new correction */
	async addCorrection(data: {
		section: string;
		correction_type?: string;
		correction_text: string;
	}): Promise<{ id: number; status: string }> {
		return this.post<{ id: number; status: string }>(
			ApiService.Endpoint.StrategyCorrections,
			data,
		);
	}

	/** Edit a correction's text */
	async editCorrection(
		id: number,
		correction_text: string,
	): Promise<{ id: number; correction_text: string }> {
		return this.patch<{ id: number; correction_text: string }>(
			ApiService.Endpoint.StrategyCorrection(id),
			{ correction_text },
		);
	}

	/** Delete a correction */
	async deleteCorrection(id: number): Promise<void> {
		return this.delete<void>(ApiService.Endpoint.StrategyCorrection(id));
	}

	/** Submit all corrections and trigger regeneration */
	async submitAllCorrections(): Promise<{
		status: string;
		message: string;
		corrections_count: number;
	}> {
		return this.post<{
			status: string;
			message: string;
			corrections_count: number;
		}>(ApiService.Endpoint.StrategyCorrectionsSubmitAll);
	}

	/** Edit a strategy section */
	async editStrategySection(
		sectionId: string,
		content: string,
	): Promise<SectionEditResponse> {
		return this.put<SectionEditResponse>(
			ApiService.Endpoint.StrategyEditSection(sectionId),
			{ content },
		);
	}

	/** Regenerate a single strategy section */
	async regenerateStrategySection(
		sectionId: string,
		feedback?: string,
	): Promise<RegenerateResponse> {
		return this.post<RegenerateResponse>(
			ApiService.Endpoint.StrategyRegenerateSection(sectionId),
			{ feedback },
		);
	}

	/** Regenerate the entire strategy */
	async regenerateStrategy(feedback?: string): Promise<RegenerateResponse> {
		return this.post<RegenerateResponse>(
			ApiService.Endpoint.StrategyRegenerate,
			{ feedback },
		);
	}

	/** Start over with the strategy */
	async startOver(
		complaints: string,
		redoInterview?: boolean,
	): Promise<StartOverResponse> {
		return this.post<StartOverResponse>(ApiService.Endpoint.StrategyStartOver, {
			complaints,
			redo_interview: redoInterview,
		});
	}

	/** Get all strategy versions for the current user */
	async getStrategyVersions(): Promise<VersionListResponse> {
		return this.get<VersionListResponse>(ApiService.Endpoint.StrategyVersions);
	}

	/** Get a specific strategy version */
	async getStrategyVersion(version: number): Promise<VersionDetailResponse> {
		return this.get<VersionDetailResponse>(
			ApiService.Endpoint.StrategyVersion(version),
		);
	}

	/** Revert to a specific strategy version */
	async revertStrategyVersion(version: number): Promise<RevertResponse> {
		return this.post<RevertResponse>(
			ApiService.Endpoint.StrategyRevert(version),
		);
	}

	// -----------------------------------------------------------------------
	// Strategy Q&A
	// -----------------------------------------------------------------------

	/** List Q&A conversations for the current strategy */
	async listStrategyQAConversations(): Promise<
		import("@/types/strategy-qa").QAConversationListResponse
	> {
		return this.get(ApiService.Endpoint.StrategyQAConversations);
	}

	/** Start a new Q&A conversation */
	async startStrategyQAConversation(
		topic?: string,
	): Promise<import("@/types/strategy-qa").QAConversation> {
		return this.post(ApiService.Endpoint.StrategyQAConversations, {
			topic: topic || null,
		});
	}

	/** Get a specific Q&A conversation */
	async getStrategyQAConversation(
		conversationId: number,
	): Promise<import("@/types/strategy-qa").QAConversation> {
		return this.get(ApiService.Endpoint.StrategyQAConversation(conversationId));
	}

	/** Send a message in a Q&A conversation */
	async sendStrategyQAMessage(
		conversationId: number,
		content: string,
	): Promise<import("@/types/strategy-qa").QAConversation> {
		return this.post(ApiService.Endpoint.StrategyQAMessage(conversationId), {
			content,
		});
	}

	/** Validate a magic link token (unauthenticated) */
	async validateMagicLink(token: string): Promise<MagicLinkValidateResponse> {
		// Use raw fetch since user is not authenticated
		const response = await fetch(
			`${this.baseURL}${ApiService.Endpoint.MagicLinkValidate}`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token }),
			},
		);
		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new Error(body.detail || "Magic link validation failed");
		}
		return response.json();
	}
	// Task Management
	// -----------------------------------------------------------------------

	/** Get paginated, filtered task list */
	async getTasks(
		campaignId: number,
		params?: TaskListParams,
	): Promise<TaskListResponse> {
		const sp = new URLSearchParams();
		if (params?.status) sp.set("status", params.status);
		if (params?.priority) sp.set("priority", params.priority);
		if (params?.category) sp.set("category", params.category);
		if (params?.source) sp.set("source", params.source);
		if (params?.assigned_volunteer_id != null)
			sp.set("assigned_volunteer_id", String(params.assigned_volunteer_id));
		if (params?.assigned_team_member_id != null)
			sp.set("assigned_team_member_id", String(params.assigned_team_member_id));
		if (params?.related_contact_id != null)
			sp.set("related_contact_id", String(params.related_contact_id));
		if (params?.is_overdue != null)
			sp.set("is_overdue", String(params.is_overdue));
		if (params?.has_due_date != null)
			sp.set("has_due_date", String(params.has_due_date));
		if (params?.due_date_from) sp.set("due_date_from", params.due_date_from);
		if (params?.due_date_to) sp.set("due_date_to", params.due_date_to);
		if (params?.search) sp.set("search", params.search);
		if (params?.sort_by) sp.set("sort_by", params.sort_by);
		if (params?.sort_dir) sp.set("sort_dir", params.sort_dir);
		if (params?.page) sp.set("page", String(params.page));
		if (params?.page_size) sp.set("page_size", String(params.page_size));
		const q = sp.toString();
		return this.get<TaskListResponse>(
			`${ApiService.Endpoint.Tasks(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/** Get a single task by ID */
	async getTask(campaignId: number, taskId: number): Promise<Task> {
		return this.get<Task>(ApiService.Endpoint.Task(campaignId, taskId));
	}

	/** Create a new task */
	async createTask(campaignId: number, data: TaskCreate): Promise<Task> {
		return this.post<Task>(ApiService.Endpoint.Tasks(campaignId), data);
	}

	/** Partially update a task */
	async updateTask(
		campaignId: number,
		taskId: number,
		data: TaskUpdate,
	): Promise<Task> {
		return this.patch<Task>(ApiService.Endpoint.Task(campaignId, taskId), data);
	}

	/** Soft-delete a task */
	async deleteTask(campaignId: number, taskId: number): Promise<void> {
		return this.delete(ApiService.Endpoint.Task(campaignId, taskId));
	}

	/** Calendar range query */
	async getTasksCalendar(
		campaignId: number,
		start: string,
		end: string,
		filters?: { status?: string; category?: string },
	): Promise<TaskCalendarResponse> {
		const sp = new URLSearchParams({ start, end });
		if (filters?.status) sp.set("status", filters.status);
		if (filters?.category) sp.set("category", filters.category);
		return this.get<TaskCalendarResponse>(
			`${ApiService.Endpoint.TasksCalendar(campaignId)}?${sp}`,
		);
	}

	/** Bulk-update task statuses */
	async bulkUpdateTaskStatus(
		campaignId: number,
		taskIds: number[],
		status: string,
	): Promise<BulkStatusUpdateResponse> {
		return this.patch<BulkStatusUpdateResponse>(
			ApiService.Endpoint.TasksBulkStatus(campaignId),
			{ task_ids: taskIds, status },
		);
	}

	// -----------------------------------------------------------------------
	// Dashboard
	// -----------------------------------------------------------------------

	/** Get aggregated dashboard data for a campaign */
	async getDashboard(campaignId: number): Promise<DashboardResponse> {
		return this.get<DashboardResponse>(
			ApiService.Endpoint.Dashboard(campaignId),
		);
	}

	// -----------------------------------------------------------------------
	// NBA (Next Best Action)
	// -----------------------------------------------------------------------

	/** Get NBA action recommendations for a campaign */
	async getNbaActions(
		campaignId: number,
		contextType: string = "dashboard",
		limit: number = 2,
	): Promise<NbaResponse> {
		return this.get<NbaResponse>(ApiService.Endpoint.NbaActions(campaignId), {
			context_type: contextType,
			limit,
		});
	}

	// -----------------------------------------------------------------------
	// Xander AI Assistant
	// -----------------------------------------------------------------------

	async getXanderSession(campaignId: number): Promise<XanderSession> {
		return this.get<XanderSession>(
			ApiService.Endpoint.XanderSession(campaignId),
		);
	}

	async getXanderSuggestions(
		campaignId: number,
		pageKey?: string,
	): Promise<XanderSuggestedPrompts> {
		return this.get<XanderSuggestedPrompts>(
			ApiService.Endpoint.XanderSuggestions(campaignId),
			pageKey ? { page_key: pageKey } : undefined,
		);
	}

	async getXanderLightbulb(campaignId: number): Promise<XanderLightbulbState> {
		return this.get<XanderLightbulbState>(
			ApiService.Endpoint.XanderLightbulb(campaignId),
		);
	}

	async getXanderSessions(
		campaignId: number,
	): Promise<{ sessions: XanderSessionListItem[] }> {
		return this.get<{ sessions: XanderSessionListItem[] }>(
			ApiService.Endpoint.XanderSessions(campaignId),
		);
	}

	async createXanderSession(campaignId: number): Promise<XanderSession> {
		return this.post<XanderSession>(
			ApiService.Endpoint.XanderSessions(campaignId),
		);
	}

	async getXanderSessionById(
		campaignId: number,
		sessionId: number,
	): Promise<XanderSession> {
		return this.get<XanderSession>(
			ApiService.Endpoint.XanderSessionById(campaignId, sessionId),
		);
	}

	async confirmXanderAction(
		campaignId: number,
		messageId: number,
		actionIndex: number,
	): Promise<{
		success: boolean;
		message: string;
		data?: Record<string, unknown>;
	}> {
		return this.post<{
			success: boolean;
			message: string;
			data?: Record<string, unknown>;
		}>(
			ApiService.Endpoint.XanderActionConfirm(
				campaignId,
				messageId,
				actionIndex,
			),
		);
	}

	async cancelXanderAction(
		campaignId: number,
		messageId: number,
		actionIndex: number,
	): Promise<{ success: boolean; message: string }> {
		return this.post<{ success: boolean; message: string }>(
			ApiService.Endpoint.XanderActionCancel(
				campaignId,
				messageId,
				actionIndex,
			),
		);
	}

	// -----------------------------------------------------------------------
	// Donations
	// -----------------------------------------------------------------------

	/** Create a new donation */
	async createDonation(
		campaignId: number,
		data: DonationCreate,
	): Promise<DonationResponse> {
		return this.post<DonationResponse>(
			ApiService.Endpoint.Donations(campaignId),
			data,
		);
	}

	/** List donations with filters and pagination */
	async listDonations(
		campaignId: number,
		filters: DonationFilters,
	): Promise<DonationListResponse> {
		const params = new URLSearchParams();
		params.set("page", String(filters.page));
		params.set("page_size", String(filters.page_size));
		if (filters.type) params.set("type", filters.type);
		if (filters.status) params.set("status", filters.status);
		if (filters.date_from) params.set("date_from", filters.date_from);
		if (filters.date_to) params.set("date_to", filters.date_to);
		return this.get<DonationListResponse>(
			`${ApiService.Endpoint.Donations(campaignId)}?${params.toString()}`,
		);
	}

	/** Get a single donation by ID */
	async getDonation(
		campaignId: number,
		donationId: number,
	): Promise<DonationResponse> {
		return this.get<DonationResponse>(
			ApiService.Endpoint.DonationDetail(campaignId, donationId),
		);
	}

	/** Update a donation */
	async updateDonation(
		campaignId: number,
		donationId: number,
		data: DonationUpdate,
	): Promise<DonationResponse> {
		return this.patch<DonationResponse>(
			ApiService.Endpoint.DonationDetail(campaignId, donationId),
			data,
		);
	}

	/** Delete a donation */
	async deleteDonation(campaignId: number, donationId: number): Promise<void> {
		return this.delete(
			ApiService.Endpoint.DonationDetail(campaignId, donationId),
		);
	}

	/** Get donation summary statistics */
	async getDonationSummary(campaignId: number): Promise<DonationSummary> {
		return this.get<DonationSummary>(
			ApiService.Endpoint.DonationSummary(campaignId),
		);
	}

	// -----------------------------------------------------------------------
	// Daily Briefing
	// -----------------------------------------------------------------------

	/** List briefings with optional date range and pagination */
	async listBriefings(
		campaignId: number,
		params?: BriefingListParams,
	): Promise<BriefingListResponse> {
		const sp = new URLSearchParams();
		if (params?.date_from) sp.set("date_from", params.date_from);
		if (params?.date_to) sp.set("date_to", params.date_to);
		if (params?.page) sp.set("page", String(params.page));
		if (params?.page_size) sp.set("page_size", String(params.page_size));
		const q = sp.toString();
		return this.get<BriefingListResponse>(
			`${ApiService.Endpoint.Briefings(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/** Get or create today's briefing */
	async getTodayBriefing(campaignId: number): Promise<Briefing> {
		return this.get<Briefing>(ApiService.Endpoint.BriefingToday(campaignId));
	}

	/** Check for a pending briefing */
	async checkPendingBriefing(
		campaignId: number,
	): Promise<PendingCheckResponse> {
		return this.get<PendingCheckResponse>(
			ApiService.Endpoint.BriefingCheckPending(campaignId),
		);
	}

	/** Generate an AI briefing */
	async generateBriefing(campaignId: number): Promise<Briefing> {
		return this.post<Briefing>(
			ApiService.Endpoint.BriefingGenerate(campaignId),
		);
	}

	/** Get a single briefing by ID */
	async getBriefing(campaignId: number, briefingId: number): Promise<Briefing> {
		return this.get<Briefing>(
			ApiService.Endpoint.BriefingDetail(campaignId, briefingId),
		);
	}

	/** Update briefing status */
	async updateBriefingStatus(
		campaignId: number,
		briefingId: number,
		status: string,
	): Promise<Briefing> {
		return this.patch<Briefing>(
			ApiService.Endpoint.BriefingStatus(campaignId, briefingId),
			{ status },
		);
	}

	/** Update a single briefing item */
	async updateBriefingItem(
		campaignId: number,
		briefingId: number,
		itemIndex: number,
		data: BriefingItemUpdate,
	): Promise<Briefing> {
		return this.patch<Briefing>(
			ApiService.Endpoint.BriefingItem(campaignId, briefingId, itemIndex),
			data,
		);
	}

	/** Bulk-update briefing items */
	async bulkUpdateBriefingItems(
		campaignId: number,
		briefingId: number,
		updates: BulkBriefingItemUpdate[],
	): Promise<Briefing> {
		return this.post<Briefing>(
			ApiService.Endpoint.BriefingItemsBulk(campaignId, briefingId),
			{ updates },
		);
	}

	/** Send a conversation message to the briefing */
	async sendBriefingMessage(
		campaignId: number,
		briefingId: number,
		message: string,
	): Promise<ConversationResponse> {
		return this.post<ConversationResponse>(
			ApiService.Endpoint.BriefingConversation(campaignId, briefingId),
			{ message },
		);
	}

	/** Get the conversation for a briefing */
	async getBriefingConversation(
		campaignId: number,
		briefingId: number,
	): Promise<ConversationResponse> {
		return this.get<ConversationResponse>(
			ApiService.Endpoint.BriefingConversation(campaignId, briefingId),
		);
	}

	/** Get briefing stats for progress visualization */
	async getBriefingStats(campaignId: number): Promise<BriefingStats> {
		return this.get<BriefingStats>(
			ApiService.Endpoint.BriefingStats(campaignId),
		);
	}

	// -----------------------------------------------------------------------
	// Email Campaigns
	// -----------------------------------------------------------------------

	/** List email campaigns with filters and pagination */
	async listEmailCampaigns(
		campaignId: number,
		filters: EmailCampaignFilters,
	): Promise<EmailCampaignListResponse> {
		const sp = new URLSearchParams();
		sp.set("page", String(filters.page));
		sp.set("per_page", String(filters.per_page));
		if (filters.status) sp.set("status", filters.status);
		if (filters.search) sp.set("search", filters.search);
		if (filters.sort) sp.set("sort", filters.sort);
		return this.get<EmailCampaignListResponse>(
			`${ApiService.Endpoint.EmailCampaigns(campaignId)}?${sp}`,
		);
	}

	/** Get a single email campaign by ID */
	async getEmailCampaign(
		campaignId: number,
		emailCampaignId: number,
	): Promise<EmailCampaign> {
		return this.get<EmailCampaign>(
			ApiService.Endpoint.EmailCampaign(campaignId, emailCampaignId),
		);
	}

	/** Create a new email campaign draft */
	async createEmailCampaign(
		campaignId: number,
		data: EmailCampaignCreateRequest,
	): Promise<EmailCampaign> {
		return this.post<EmailCampaign>(
			ApiService.Endpoint.EmailCampaigns(campaignId),
			data,
		);
	}
	// Team Management
	// -----------------------------------------------------------------------

	/** List campaign team members with filters and pagination */
	async getTeamUsers(
		campaignId: number,
		params?: TeamListParams,
	): Promise<CampaignUserListResponse> {
		const sp = new URLSearchParams();
		if (params?.status) sp.set("status", params.status);
		if (params?.search) sp.set("search", params.search);
		if (params?.sort_by) sp.set("sort_by", params.sort_by);
		if (params?.sort_order) sp.set("sort_order", params.sort_order);
		if (params?.page) sp.set("page", String(params.page));
		if (params?.per_page) sp.set("per_page", String(params.per_page));
		const q = sp.toString();
		return this.get<CampaignUserListResponse>(
			`${ApiService.Endpoint.TeamUsers(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/** Invite a user to the campaign */
	async inviteTeamUser(
		campaignId: number,
		data: InviteUserRequest,
	): Promise<InviteResponse> {
		return this.post<InviteResponse>(
			ApiService.Endpoint.TeamInvite(campaignId),
			data,
		);
	}

	/** Update an email campaign */
	async updateEmailCampaign(
		campaignId: number,
		emailCampaignId: number,
		data: EmailCampaignUpdateRequest,
	): Promise<EmailCampaign> {
		return this.patch<EmailCampaign>(
			ApiService.Endpoint.EmailCampaign(campaignId, emailCampaignId),
			data,
		);
	}
	/** Change a team member's role */
	async changeTeamUserRole(
		campaignId: number,
		userId: number,
		data: RoleChangeRequest,
	): Promise<RoleChangeResponse> {
		return this.patch<RoleChangeResponse>(
			ApiService.Endpoint.TeamUserRole(campaignId, userId),
			data,
		);
	}

	/** Delete an email campaign */
	async deleteEmailCampaign(
		campaignId: number,
		emailCampaignId: number,
	): Promise<{ message: string }> {
		return this.delete<{ message: string }>(
			ApiService.Endpoint.EmailCampaign(campaignId, emailCampaignId),
		);
	}

	/** Duplicate an email campaign */
	async duplicateEmailCampaign(
		campaignId: number,
		emailCampaignId: number,
	): Promise<EmailCampaign> {
		return this.post<EmailCampaign>(
			ApiService.Endpoint.EmailCampaignDuplicate(campaignId, emailCampaignId),
		);
	}

	/** Generate AI draft for an email campaign */
	async generateEmailDraft(
		campaignId: number,
		emailCampaignId: number,
		data: GenerateDraftRequest,
	): Promise<DraftResult> {
		return this.post<DraftResult>(
			ApiService.Endpoint.EmailCampaignGenerateDraft(
				campaignId,
				emailCampaignId,
			),
			data,
		);
	}

	/** Send an email campaign */
	async sendEmailCampaign(
		campaignId: number,
		emailCampaignId: number,
	): Promise<SendResult> {
		return this.post<SendResult>(
			ApiService.Endpoint.EmailCampaignSend(campaignId, emailCampaignId),
		);
	}

	/** Schedule an email campaign */
	async scheduleEmailCampaign(
		campaignId: number,
		emailCampaignId: number,
		data: ScheduleRequest,
	): Promise<ScheduleResult> {
		return this.post<ScheduleResult>(
			ApiService.Endpoint.EmailCampaignSchedule(campaignId, emailCampaignId),
			data,
		);
	}
	/** Deactivate a team member */
	async deactivateTeamUser(
		campaignId: number,
		userId: number,
		data?: DeactivateRequest,
	): Promise<DeactivateResponse> {
		return this.post<DeactivateResponse>(
			ApiService.Endpoint.TeamUserDeactivate(campaignId, userId),
			data ?? {},
		);
	}

	/** Reactivate a deactivated team member */
	async reactivateTeamUser(
		campaignId: number,
		userId: number,
		data?: ReactivateRequest,
	): Promise<ReactivateResponse> {
		return this.post<ReactivateResponse>(
			ApiService.Endpoint.TeamUserReactivate(campaignId, userId),
			data ?? {},
		);
	}

	/** Bulk deactivate multiple team members */
	async bulkDeactivateTeamUsers(
		campaignId: number,
		data: BulkDeactivateRequest,
	): Promise<BulkDeactivateResponse> {
		return this.post<BulkDeactivateResponse>(
			ApiService.Endpoint.TeamBulkDeactivate(campaignId),
			data,
		);
	}

	/** Cancel a scheduled email campaign */
	async cancelEmailCampaign(
		campaignId: number,
		emailCampaignId: number,
	): Promise<CancelResult> {
		return this.post<CancelResult>(
			ApiService.Endpoint.EmailCampaignCancel(campaignId, emailCampaignId),
		);
	}

	/** Reset a failed email campaign back to draft */
	async resetEmailCampaignToDraft(
		campaignId: number,
		emailCampaignId: number,
	): Promise<EmailCampaign> {
		return this.post<EmailCampaign>(
			ApiService.Endpoint.EmailCampaignResetToDraft(
				campaignId,
				emailCampaignId,
			),
		);
	}

	/** Preview audience for email campaign (before creation) */
	async previewEmailAudience(
		campaignId: number,
		audiences: AudienceItem[],
		exclusions: AudienceItem[] = [],
	): Promise<AudiencePreviewResult> {
		return this.post<AudiencePreviewResult>(
			ApiService.Endpoint.EmailCampaignAudiencePreview(campaignId),
			{ audiences, exclusions },
		);
	}

	/** Get send preview for an email campaign */
	async getEmailSendPreview(
		campaignId: number,
		emailCampaignId: number,
	): Promise<SendPreview> {
		return this.get<SendPreview>(
			ApiService.Endpoint.EmailCampaignSendPreview(campaignId, emailCampaignId),
		);
	}

	/** Get per-recipient rendered previews with merge-field warnings */
	async getEmailRecipientPreviews(
		campaignId: number,
		emailCampaignId: number,
		page = 1,
		perPage = 25,
	): Promise<RecipientPreviewResponse> {
		const sp = new URLSearchParams({
			page: String(page),
			per_page: String(perPage),
		});
		return this.get<RecipientPreviewResponse>(
			`${ApiService.Endpoint.EmailCampaignRecipientPreviews(campaignId, emailCampaignId)}?${sp}`,
		);
	}

	/** Get rendered HTML preview for an email campaign */
	async getEmailHtmlPreview(
		campaignId: number,
		emailCampaignId: number,
	): Promise<{ html: string }> {
		return this.post<{ html: string }>(
			ApiService.Endpoint.EmailCampaignPreview(campaignId, emailCampaignId),
		);
	}

	/** Get performance metrics for a sent email campaign */
	async getEmailCampaignMetrics(
		campaignId: number,
		emailCampaignId: number,
	): Promise<CampaignMetrics> {
		return this.get<CampaignMetrics>(
			ApiService.Endpoint.EmailCampaignMetrics(campaignId, emailCampaignId),
		);
	}

	/** Get paginated recipient list for an email campaign */
	async getEmailCampaignRecipients(
		campaignId: number,
		emailCampaignId: number,
		filters: RecipientFilters,
	): Promise<RecipientListResponse> {
		const sp = new URLSearchParams();
		if (filters.status) sp.set("status", filters.status);
		sp.set("page", String(filters.page));
		sp.set("per_page", String(filters.per_page));
		return this.get<RecipientListResponse>(
			`${ApiService.Endpoint.EmailCampaignRecipients(campaignId, emailCampaignId)}?${sp}`,
		);
	}

	/** Get email suggestions and performance for the daily briefing */
	async getEmailBriefingData(campaignId: number): Promise<{
		suggestions: EmailBriefingSuggestion[];
		performance: EmailPerformanceSummary;
	}> {
		return this.get<{
			suggestions: EmailBriefingSuggestion[];
			performance: EmailPerformanceSummary;
		}>(ApiService.Endpoint.EmailCampaignBriefingData(campaignId));
	}

	/** Create an email campaign from a daily briefing suggestion */
	async createEmailFromSuggestion(
		campaignId: number,
		data: CreateFromSuggestionRequest,
	): Promise<CreateFromSuggestionResponse> {
		return this.post<CreateFromSuggestionResponse>(
			ApiService.Endpoint.EmailCampaignFromSuggestion(campaignId),
			data,
		);
	}

	// -----------------------------------------------------------------------
	// Email Templates
	// -----------------------------------------------------------------------

	/** List available email templates with preview HTML */
	async listEmailTemplates(
		campaignId: number,
	): Promise<EmailTemplateListResponse> {
		return this.get<EmailTemplateListResponse>(
			ApiService.Endpoint.EmailTemplates(campaignId),
		);
	}

	/** Preview a specific email template with sample content */
	async previewEmailTemplate(
		campaignId: number,
		templateId: string,
	): Promise<EmailTemplatePreview> {
		return this.get<EmailTemplatePreview>(
			ApiService.Endpoint.EmailTemplatePreview(campaignId, templateId),
		);
	}

	// -----------------------------------------------------------------------
	// Email Settings (candidate-facing email configuration)
	// -----------------------------------------------------------------------

	/** Get email settings (returns provisioned account or platform defaults) */
	async getEmailSettings(campaignId: number): Promise<EmailSettings> {
		return this.get<EmailSettings>(
			ApiService.Endpoint.EmailSettings(campaignId),
		);
	}

	/** Provision a new email account for the campaign */
	async provisionEmailSettings(
		campaignId: number,
		data: ProvisionEmailRequest,
	): Promise<EmailSettings> {
		return this.post<EmailSettings>(
			ApiService.Endpoint.EmailSettings(campaignId),
			data,
		);
	}

	/** Update sender defaults for an existing email account */
	async updateEmailSettings(
		campaignId: number,
		data: UpdateEmailSettingsRequest,
	): Promise<EmailSettings> {
		return this.patch<EmailSettings>(
			ApiService.Endpoint.EmailSettings(campaignId),
			data,
		);
	}

	/** Disconnect the email account for a campaign */
	async disconnectEmailSettings(
		campaignId: number,
	): Promise<{ message: string }> {
		return this.delete<{ message: string }>(
			ApiService.Endpoint.EmailSettings(campaignId),
		);
	}
	/** List pending invitations for the campaign */
	async getTeamInvites(
		campaignId: number,
		page = 1,
		perPage = 20,
	): Promise<InviteListResponse> {
		const sp = new URLSearchParams({
			page: String(page),
			per_page: String(perPage),
		});
		return this.get<InviteListResponse>(
			`${ApiService.Endpoint.TeamInvites(campaignId)}?${sp}`,
		);
	}

	/** Resend a pending invitation */
	async resendTeamInvite(
		campaignId: number,
		inviteId: number,
	): Promise<InviteResendResponse> {
		return this.post<InviteResendResponse>(
			ApiService.Endpoint.TeamInviteResend(campaignId, inviteId),
		);
	}

	/** Revoke a pending invitation */
	async revokeTeamInvite(
		campaignId: number,
		inviteId: number,
	): Promise<InviteRevokeResponse> {
		return this.delete<InviteRevokeResponse>(
			ApiService.Endpoint.TeamInviteRevoke(campaignId, inviteId),
		);
	}

	/** Get role change history for the campaign */
	async getTeamRoleHistory(
		campaignId: number,
		params?: {
			user_id?: number;
			action?: string;
			page?: number;
			per_page?: number;
		},
	): Promise<RoleHistoryResponse> {
		const sp = new URLSearchParams();
		if (params?.user_id) sp.set("user_id", String(params.user_id));
		if (params?.action) sp.set("action", params.action);
		if (params?.page) sp.set("page", String(params.page));
		if (params?.per_page) sp.set("per_page", String(params.per_page));
		const q = sp.toString();
		return this.get<RoleHistoryResponse>(
			`${ApiService.Endpoint.TeamRoleHistory(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	// -----------------------------------------------------------------------
	// Call Time
	// -----------------------------------------------------------------------

	/**
	 * Get a paginated page of the call list (PRD-42).
	 *
	 * Default behaviour: 50 entries, manual sort, default status filter
	 * (pending + completed). Returns the new paginated envelope.
	 */
	async getCallTimeList(
		campaignId: number,
		params: CallTimeListParams = {},
	): Promise<PaginatedCallTimeListResponse> {
		const sp = new URLSearchParams();
		if (params.purpose) sp.set("purpose", params.purpose);
		if (params.search) sp.set("search", params.search);
		if (params.sortBy) sp.set("sort_by", params.sortBy);
		if (params.status) sp.set("status", params.status);
		if (params.priority) sp.set("priority", params.priority);
		if (params.page != null) sp.set("page", String(params.page));
		if (params.perPage != null) sp.set("per_page", String(params.perPage));
		const q = sp.toString();
		return this.get<PaginatedCallTimeListResponse>(
			`${ApiService.Endpoint.CallTimeList(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/**
	 * Pre-PRD-42 contract: bare list of entries, capped at `limit`. Kept for
	 * call sites that have not yet migrated to the paginated envelope; hits
	 * the same endpoint with `?legacy=1`. Remove once all callers are
	 * upgraded.
	 */
	async getCallTimeListLegacy(
		campaignId: number,
		purpose?: string,
	): Promise<CallTimeListEntry[]> {
		const sp = new URLSearchParams({ legacy: "1" });
		if (purpose) sp.set("purpose", purpose);
		return this.get<CallTimeListEntry[]>(
			`${ApiService.Endpoint.CallTimeList(campaignId)}?${sp.toString()}`,
		);
	}

	async getSkippedCallTimeList(
		campaignId: number,
	): Promise<CallTimeListEntry[]> {
		return this.get<CallTimeListEntry[]>(
			ApiService.Endpoint.CallTimeSkippedList(campaignId),
		);
	}

	// -----------------------------------------------------------------------
	// Recipes (PRD-42 §5.4)
	// -----------------------------------------------------------------------

	async listCallTimeRecipes(
		campaignId: number,
		params: { page?: number; perPage?: number } = {},
	): Promise<PaginatedRecipeResponse> {
		const sp = new URLSearchParams();
		if (params.page != null) sp.set("page", String(params.page));
		if (params.perPage != null) sp.set("per_page", String(params.perPage));
		const q = sp.toString();
		return this.get<PaginatedRecipeResponse>(
			`${ApiService.Endpoint.CallTimeRecipes(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	async createCallTimeRecipe(
		campaignId: number,
		body: RecipeCreateRequest,
	): Promise<Recipe> {
		return this.post<Recipe>(
			ApiService.Endpoint.CallTimeRecipes(campaignId),
			body,
		);
	}

	async updateCallTimeRecipe(
		campaignId: number,
		recipeId: number,
		body: RecipeUpdateRequest,
	): Promise<Recipe> {
		return this.patch<Recipe>(
			ApiService.Endpoint.CallTimeRecipe(campaignId, recipeId),
			body,
		);
	}

	async deleteCallTimeRecipe(
		campaignId: number,
		recipeId: number,
	): Promise<void> {
		return this.delete(
			ApiService.Endpoint.CallTimeRecipe(campaignId, recipeId),
		);
	}

	async applyCallTimeRecipe(
		campaignId: number,
		recipeId: number,
	): Promise<RecipeApplyResponse> {
		return this.post<RecipeApplyResponse>(
			ApiService.Endpoint.CallTimeRecipeApply(campaignId, recipeId),
			{},
		);
	}

	// PRD-42 §5.3 — recompute is dispatched to the background worker (one
	// Gemini call per entry would time out inline), so this returns 202 with
	// a queued status rather than a synchronous count.
	async recomputeCallTimeScores(
		campaignId: number,
	): Promise<{ status: string; task_id: string | null; message: string }> {
		return this.post<{
			status: string;
			task_id: string | null;
			message: string;
		}>(ApiService.Endpoint.CallTimeRecomputeScores(campaignId), {});
	}

	/** Get full contact context for a call list entry */
	async getContactContext(
		campaignId: number,
		listEntryId: number,
	): Promise<ContactContext> {
		return this.get<ContactContext>(
			ApiService.Endpoint.CallTimeListContext(campaignId, listEntryId),
		);
	}

	/** Regenerate AI talking points for a call list entry */
	async regenerateTalkingPoints(
		campaignId: number,
		listEntryId: number,
	): Promise<{ talking_points: string | null }> {
		return this.get<{ talking_points: string | null }>(
			ApiService.Endpoint.CallTimeListTalkingPoints(campaignId, listEntryId),
		);
	}

	/** Log a completed call */
	async logCall(
		campaignId: number,
		data: CallTimeEntryCreate,
	): Promise<CallTimeEntry> {
		return this.post<CallTimeEntry>(
			ApiService.Endpoint.CallTimeEntries(campaignId),
			data,
		);
	}
	// Text Banking Campaigns
	// -----------------------------------------------------------------------

	/** List text campaigns with optional filters */
	async listTextCampaigns(
		campaignId: number,
		filters?: TextCampaignFilters,
	): Promise<TextCampaign[]> {
		const sp = new URLSearchParams();
		if (filters?.status) sp.set("status", filters.status);
		if (filters?.mode) sp.set("mode", filters.mode);
		const q = sp.toString();
		return this.get<TextCampaign[]>(
			`${ApiService.Endpoint.TextCampaigns(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/** Get a single text campaign by ID */
	async getTextCampaign(
		campaignId: number,
		tcId: number,
	): Promise<TextCampaign> {
		return this.get<TextCampaign>(
			ApiService.Endpoint.TextCampaign(campaignId, tcId),
		);
	}

	/** Create a new text campaign draft */
	async createTextCampaign(
		campaignId: number,
		data: TextCampaignCreate,
	): Promise<TextCampaign> {
		return this.post<TextCampaign>(
			ApiService.Endpoint.TextCampaigns(campaignId),
			data,
		);
	}

	/** Update the status of a call list entry (skip / defer) */
	async updateCallListStatus(
		campaignId: number,
		listEntryId: number,
		status: string,
	): Promise<CallTimeListEntry> {
		return this.patch<CallTimeListEntry>(
			ApiService.Endpoint.CallTimeListStatus(campaignId, listEntryId),
			{ status },
		);
	}

	/** Get the caller's session stats (today + this week) */
	async getCallTimeStats(campaignId: number): Promise<CallTimeStats> {
		return this.get<CallTimeStats>(
			ApiService.Endpoint.CallTimeStats(campaignId),
		);
	}

	/** Get the weekly call goal for a campaign */
	async getCallTimeGoal(campaignId: number): Promise<CallTimeGoalResponse> {
		return this.get<CallTimeGoalResponse>(
			ApiService.Endpoint.CallTimeGoal(campaignId),
		);
	}

	// -----------------------------------------------------------------------
	// Call Time Management (List curation — S09)
	// -----------------------------------------------------------------------

	/** Add a single contact to the call list */
	async addToCallList(
		campaignId: number,
		data: {
			contact_id: number;
			priority: string;
			purpose?: string;
			notes?: string;
		},
	): Promise<CallTimeListEntry> {
		return this.post<CallTimeListEntry>(
			`/campaigns/${campaignId}/call-time/manage/list`,
			data,
		);
	}

	/** Update a text campaign (draft only) */
	async updateTextCampaign(
		campaignId: number,
		tcId: number,
		data: TextCampaignUpdate,
	): Promise<TextCampaign> {
		return this.patch<TextCampaign>(
			ApiService.Endpoint.TextCampaign(campaignId, tcId),
			data,
		);
	}

	/** Bulk-add contacts from an action group to the call list */
	async bulkAddFromGroup(
		campaignId: number,
		data: {
			action_group_id: number;
			purpose?: string;
			priority: string;
		},
	): Promise<{ added: number; message: string }> {
		return this.post<{ added: number; message: string }>(
			`/campaigns/${campaignId}/call-time/manage/list/bulk-add`,
			data,
		);
	}

	/** Update priority / purpose / notes for a call list entry */
	async updateCallListEntry(
		campaignId: number,
		listEntryId: number,
		data: { priority?: string; purpose?: string; notes?: string },
	): Promise<CallTimeListEntry> {
		return this.patch<CallTimeListEntry>(
			`/campaigns/${campaignId}/call-time/manage/list/${listEntryId}`,
			data,
		);
	}

	/** Remove a contact from the call list */
	async deleteCallListEntry(
		campaignId: number,
		listEntryId: number,
	): Promise<void> {
		return this.delete(
			`/campaigns/${campaignId}/call-time/manage/list/${listEntryId}`,
		);
	}

	/** Reorder the call list (save drag-and-drop result) */
	async reorderCallList(
		campaignId: number,
		orderedIds: number[],
	): Promise<{ reordered: number }> {
		return this.put<{ reordered: number }>(
			`/campaigns/${campaignId}/call-time/manage/list/reorder`,
			{ ordered_ids: orderedIds },
		);
	}
	/**
	 * Launch a text campaign. When `acknowledgePartial` is true the request
	 * carries `acknowledge_partial: true`, telling the backend to proceed past
	 * an `INSUFFICIENT_CREDITS` (partial-send) low-balance warning (BILL-11).
	 * It does NOT override a hard `OUT_OF_CREDITS` block (balance ≤ 0).
	 */
	async launchTextCampaign(
		campaignId: number,
		tcId: number,
		opts: { acknowledgePartial?: boolean } = {},
	): Promise<TextCampaign> {
		return this.post<TextCampaign>(
			ApiService.Endpoint.TextCampaignLaunch(campaignId, tcId),
			opts.acknowledgePartial ? { acknowledge_partial: true } : undefined,
		);
	}

	/** Pause an active text campaign */
	async pauseTextCampaign(
		campaignId: number,
		tcId: number,
	): Promise<TextCampaign> {
		return this.post<TextCampaign>(
			ApiService.Endpoint.TextCampaignPause(campaignId, tcId),
		);
	}

	/** Cancel a text campaign */
	async cancelTextCampaign(
		campaignId: number,
		tcId: number,
	): Promise<TextCampaign> {
		return this.post<TextCampaign>(
			ApiService.Endpoint.TextCampaignCancel(campaignId, tcId),
		);
	}

	/** Resume a paused text campaign */
	async resumeTextCampaign(
		campaignId: number,
		tcId: number,
	): Promise<TextCampaign> {
		return this.post<TextCampaign>(
			ApiService.Endpoint.TextCampaignResume(campaignId, tcId),
		);
	}

	/** Duplicate a text campaign */
	async duplicateTextCampaign(
		campaignId: number,
		tcId: number,
	): Promise<TextCampaign> {
		return this.post<TextCampaign>(
			ApiService.Endpoint.TextCampaignDuplicate(campaignId, tcId),
		);
	}

	/** Get audience preview for a text campaign */
	async getTextCampaignAudiencePreview(
		campaignId: number,
		tcId: number,
	): Promise<AudienceSnapshot> {
		return this.get<AudienceSnapshot>(
			ApiService.Endpoint.TextCampaignAudiencePreview(campaignId, tcId),
		);
	}

	// -----------------------------------------------------------------------
	// Call Time Results (S10 — Results Dashboard)
	// -----------------------------------------------------------------------

	/** Get daily call-time result rows for a date range */
	async getCallTimeResults(
		campaignId: number,
		startDate: string,
		endDate: string,
	): Promise<import("@/types/call-time").DailyResultRow[]> {
		return this.get<import("@/types/call-time").DailyResultRow[]>(
			`/campaigns/${campaignId}/call-time/results?start_date=${startDate}&end_date=${endDate}`,
		);
	}

	/** Get the weekly summary for call-time results */
	async getCallTimeWeeklySummary(
		campaignId: number,
	): Promise<import("@/types/call-time").CallTimeWeeklySummary> {
		return this.get<import("@/types/call-time").CallTimeWeeklySummary>(
			`/campaigns/${campaignId}/call-time/results/summary`,
		);
	}

	/** Update the weekly call goal */
	async updateCallTimeGoal(
		campaignId: number,
		weeklyCallGoal: number,
	): Promise<{ weekly_call_goal: number }> {
		return this.put<{ weekly_call_goal: number }>(
			`/campaigns/${campaignId}/call-time/results/goal`,
			{ weekly_call_goal: weeklyCallGoal },
		);
	}

	// -----------------------------------------------------------------------
	// Call Time Dialer (PRD 32 — Twilio Voice)
	// -----------------------------------------------------------------------

	/** Get a Twilio Voice access token for the browser SDK */
	async getVoiceToken(campaignId: number): Promise<VoiceTokenResponse> {
		return this.get<VoiceTokenResponse>(
			ApiService.Endpoint.CallTimeVoiceToken(campaignId),
		);
	}

	/** Initiate a call to a contact */
	async dialContact(
		campaignId: number,
		data: DialRequest,
	): Promise<DialResponse> {
		return this.post<DialResponse>(
			ApiService.Endpoint.CallTimeDial(campaignId),
			data,
		);
	}

	/** End an active call */
	async hangupCall(campaignId: number): Promise<void> {
		return this.post(ApiService.Endpoint.CallTimeHangup(campaignId), {});
	}

	/** Get transcription for a completed call */
	async getTranscription(
		campaignId: number,
		entryId: number,
	): Promise<TranscriptionResponse> {
		return this.get<TranscriptionResponse>(
			ApiService.Endpoint.CallTimeTranscription(campaignId, entryId),
		);
	}

	/** Initiate caller ID verification for the candidate's phone */
	async verifyCallerId(campaignId: number): Promise<CallerIdVerifyResponse> {
		return this.post<CallerIdVerifyResponse>(
			ApiService.Endpoint.CallTimeVerifyCallerId(campaignId),
			{},
		);
	}

	// Text Banking Scripts
	// -----------------------------------------------------------------------

	/** Update a text script */
	async updateTextScript(
		campaignId: number,
		scriptId: number,
		data: TextScriptUpdate,
	): Promise<TextScript> {
		return this.patch<TextScript>(
			ApiService.Endpoint.TextScript(campaignId, scriptId),
			data,
		);
	}

	// -----------------------------------------------------------------------
	// Text Banking AI Script Generation
	// -----------------------------------------------------------------------

	/** Generate an AI-powered text message script */
	async generateAIScript(
		campaignId: number,
		data: { purpose: string; mode: string; action_group_id?: number | null },
	): Promise<TextScriptCreate> {
		return this.post<TextScriptCreate>(
			ApiService.Endpoint.TextAIGenerateScript(campaignId),
			data,
		);
	}

	/** Suggest the best action group for text outreach */
	async suggestTextAudience(campaignId: number): Promise<{
		suggestion: {
			group_id: number;
			group_name: string;
			contact_count: number;
			reason: string;
		} | null;
	}> {
		return this.post<{
			suggestion: {
				group_id: number;
				group_name: string;
				contact_count: number;
				reason: string;
			} | null;
		}>(ApiService.Endpoint.TextAISuggestAudience(campaignId));
	}

	// -----------------------------------------------------------------------
	// Text Banking P2P Queue
	// -----------------------------------------------------------------------

	/** Get the volunteer's P2P send queue */
	async getP2PSendQueue(
		campaignId: number,
		tcId: number,
	): Promise<SendQueueItem[]> {
		return this.get<SendQueueItem[]>(
			ApiService.Endpoint.TextP2PQueue(campaignId, tcId),
		);
	}

	/** Send a single P2P message from the queue */
	async sendP2PMessage(
		campaignId: number,
		tcId: number,
		messageId: number,
		data: P2PSendRequest,
	): Promise<TextMessage> {
		return this.post<TextMessage>(
			ApiService.Endpoint.TextP2PSend(campaignId, tcId, messageId),
			data,
		);
	}

	/** Request additional P2P contacts from the pool */
	async getMoreP2PContacts(
		campaignId: number,
		tcId: number,
	): Promise<TextMessage[]> {
		return this.post<TextMessage[]>(
			ApiService.Endpoint.TextP2PGetMore(campaignId, tcId),
			{},
		);
	}

	// -----------------------------------------------------------------------
	// Text Banking Volunteer Inbox (A2P reply management)
	// -----------------------------------------------------------------------

	/** Get the volunteer's assigned reply conversations */
	async getVolunteerInbox(
		campaignId: number,
		tcId: number,
	): Promise<Conversation[]> {
		return this.get<Conversation[]>(
			ApiService.Endpoint.VolunteerInbox(campaignId, tcId),
		);
	}

	/** Get full message thread for a conversation */
	async getConversationThread(
		campaignId: number,
		tcId: number,
		convId: string,
	): Promise<TextMessage[]> {
		return this.get<TextMessage[]>(
			ApiService.Endpoint.VolunteerThread(campaignId, tcId, convId),
		);
	}

	/** Send a volunteer reply to an assigned inbound message */
	async sendVolunteerReply(
		campaignId: number,
		tcId: number,
		messageId: number,
		data: VolunteerReplyRequest,
	): Promise<TextMessage> {
		return this.post<TextMessage>(
			ApiService.Endpoint.VolunteerReply(campaignId, tcId, messageId),
			data,
		);
	}

	// -----------------------------------------------------------------------
	// Text Banking Metrics
	// -----------------------------------------------------------------------

	/** Get campaign metrics (delivery, engagement, dispositions, timeline) */
	async getTextCampaignMetrics(
		campaignId: number,
		tcId: number,
	): Promise<TextCampaignMetrics> {
		return this.get<TextCampaignMetrics>(
			ApiService.Endpoint.TextCampaignMetrics(campaignId, tcId),
		);
	}

	/** Paginated campaign inbox — inbound replies to a text campaign (PRD 46 §7). */
	async getCampaignReplies(
		campaignId: number,
		tcId: number,
		page = 1,
		pageSize = 10,
	): Promise<CampaignRepliesResponse> {
		return this.get<CampaignRepliesResponse>(
			`${ApiService.Endpoint.TextCampaignReplies(campaignId, tcId)}?page=${page}&page_size=${pageSize}`,
		);
	}

	/** Resolve a conversation with a disposition */
	async resolveConversation(
		campaignId: number,
		tcId: number,
		convId: string,
		data: ResolveConversationRequest,
	): Promise<{ message: string }> {
		return this.post<{ message: string }>(
			ApiService.Endpoint.VolunteerResolve(campaignId, tcId, convId),
			data,
		);
	}

	// -----------------------------------------------------------------------
	// Phone Banking Campaigns
	// -----------------------------------------------------------------------

	/** List phone campaigns with optional status filter */
	async listPhoneCampaigns(
		campaignId: number,
		status?: string,
	): Promise<PhoneCampaignListItem[]> {
		const url = status
			? `${ApiService.Endpoint.PhoneCampaigns(campaignId)}?status=${status}`
			: ApiService.Endpoint.PhoneCampaigns(campaignId);
		return this.get<PhoneCampaignListItem[]>(url);
	}

	/** Get a single phone campaign by ID */
	async getPhoneCampaign(
		campaignId: number,
		pcId: number,
	): Promise<PhoneCampaign> {
		return this.get<PhoneCampaign>(
			ApiService.Endpoint.PhoneCampaign(campaignId, pcId),
		);
	}

	/** Create a new phone campaign */
	async createPhoneCampaign(
		campaignId: number,
		data: CreatePhoneCampaignRequest,
	): Promise<PhoneCampaign> {
		return this.post<PhoneCampaign>(
			ApiService.Endpoint.PhoneCampaigns(campaignId),
			data,
		);
	}

	/** Launch a phone campaign */
	async launchPhoneCampaign(
		campaignId: number,
		pcId: number,
	): Promise<PhoneCampaign> {
		return this.post<PhoneCampaign>(
			ApiService.Endpoint.PhoneCampaignLaunch(campaignId, pcId),
		);
	}

	/** Pause an active phone campaign */
	async pausePhoneCampaign(
		campaignId: number,
		pcId: number,
	): Promise<PhoneCampaign> {
		return this.post<PhoneCampaign>(
			ApiService.Endpoint.PhoneCampaignPause(campaignId, pcId),
		);
	}

	/** Resume a paused phone campaign */
	async resumePhoneCampaign(
		campaignId: number,
		pcId: number,
	): Promise<PhoneCampaign> {
		return this.post<PhoneCampaign>(
			ApiService.Endpoint.PhoneCampaignResume(campaignId, pcId),
		);
	}

	/** Cancel a phone campaign */
	async cancelPhoneCampaign(
		campaignId: number,
		pcId: number,
	): Promise<PhoneCampaign> {
		return this.post<PhoneCampaign>(
			ApiService.Endpoint.PhoneCampaignCancel(campaignId, pcId),
		);
	}

	/** Update a phone campaign (e.g. pause/resume via status field) */
	async updatePhoneCampaign(
		campaignId: number,
		pcId: number,
		data: PhoneCampaignUpdate,
	): Promise<PhoneCampaign> {
		return this.patch<PhoneCampaign>(
			ApiService.Endpoint.PhoneCampaign(campaignId, pcId),
			data,
		);
	}

	// -----------------------------------------------------------------------
	// Phone Banking Shifts
	// -----------------------------------------------------------------------

	/** Create a phone shift */
	async createPhoneShift(
		campaignId: number,
		pcId: number,
		data: CreatePhoneShiftRequest,
	): Promise<PhoneShift> {
		return this.post<PhoneShift>(
			ApiService.Endpoint.PhoneShifts(campaignId, pcId),
			data,
		);
	}

	/** Join a phone shift */
	async joinPhoneShift(
		campaignId: number,
		pcId: number,
		shiftId: number,
	): Promise<void> {
		return this.post<void>(
			ApiService.Endpoint.PhoneShiftJoin(campaignId, pcId, shiftId),
		);
	}

	/** Leave a phone shift */
	async leavePhoneShift(
		campaignId: number,
		pcId: number,
		shiftId: number,
	): Promise<void> {
		return this.post<void>(
			ApiService.Endpoint.PhoneShiftLeave(campaignId, pcId, shiftId),
		);
	}

	/** List shifts for a phone campaign */
	async listPhoneShifts(
		campaignId: number,
		pcId: number,
	): Promise<PhoneShift[]> {
		return this.get<PhoneShift[]>(
			ApiService.Endpoint.PhoneShifts(campaignId, pcId),
		);
	}

	/** Activate a scheduled phone shift (scheduled -> active) */
	async activatePhoneShift(
		campaignId: number,
		pcId: number,
		shiftId: number,
	): Promise<PhoneShift> {
		return this.post<PhoneShift>(
			ApiService.Endpoint.PhoneShiftActivate(campaignId, pcId, shiftId),
		);
	}

	/** Complete an active phone shift (active -> completed) */
	async completePhoneShift(
		campaignId: number,
		pcId: number,
		shiftId: number,
	): Promise<PhoneShift> {
		return this.post<PhoneShift>(
			ApiService.Endpoint.PhoneShiftComplete(campaignId, pcId, shiftId),
		);
	}

	// -----------------------------------------------------------------------
	// Phone Banking Scripts
	// -----------------------------------------------------------------------

	/** Generate an AI phone script */
	async generatePhoneScript(
		campaignId: number,
		data: GenerateScriptRequest,
	): Promise<PhoneScript> {
		return this.post<PhoneScript>(
			ApiService.Endpoint.PhoneScriptGenerate(campaignId),
			data,
		);
	}

	/** Get a phone script by ID */
	async getPhoneScript(
		campaignId: number,
		scriptId: number,
	): Promise<PhoneScript> {
		return this.get<PhoneScript>(
			ApiService.Endpoint.PhoneScript(campaignId, scriptId),
		);
	}

	/** Update a phone script */
	async updatePhoneScript(
		campaignId: number,
		scriptId: number,
		data: Partial<PhoneScript>,
	): Promise<PhoneScript> {
		return this.put<PhoneScript>(
			ApiService.Endpoint.PhoneScript(campaignId, scriptId),
			data,
		);
	}

	// -----------------------------------------------------------------------
	// Phone Banking - Calling Session
	// -----------------------------------------------------------------------

	/** Get WebRTC token */
	async getPhoneCallToken(
		campaignId: number,
		pcId: number,
	): Promise<{ token: string; identity?: string }> {
		return this.get<{ token: string; identity?: string }>(
			ApiService.Endpoint.PhoneCallToken(campaignId, pcId),
		);
	}

	/** Get next contact (returns null on 204, throws on 403 quiet hours) */
	async getNextPhoneContact(
		campaignId: number,
		pcId: number,
	): Promise<NextContactResponse | null> {
		const token = await authService.getAuthToken();
		const url = ApiService.Endpoint.PhoneCallNext(campaignId, pcId);
		const response = await fetch(`${this.baseURL}${url}`, {
			headers: {
				"Content-Type": "application/json",
				...(token && { Authorization: `Bearer ${token}` }),
			},
		});
		if (response.status === 204) return null;
		if (response.status === 403) {
			const body = await response.json();
			throw {
				status: 403,
				detail: body?.error?.detail ?? body?.detail ?? body,
			};
		}
		if (response.status === 401) {
			authService.clearTokens();
			router.navigate({ to: "/login" });
			throw new Error("Session expired");
		}
		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		return response.json();
	}

	/** Start a call */
	async startPhoneCall(
		campaignId: number,
		pcId: number,
		contactId: number,
		shiftId?: number,
	): Promise<{
		attempt_id: number;
		phone_number: string;
		caller_id_number: string;
	}> {
		return this.post(ApiService.Endpoint.PhoneCallStart(campaignId, pcId), {
			contact_id: contactId,
			shift_id: shiftId,
		});
	}

	/** Dispose (save outcome of) a call */
	async disposePhoneCall(
		campaignId: number,
		pcId: number,
		attemptId: number,
		data: DisposeCallRequest,
	): Promise<{ attempt_id: number; disposition: string; status: string }> {
		return this.post(
			ApiService.Endpoint.PhoneCallDispose(campaignId, pcId, attemptId),
			data,
		);
	}

	/** Skip a contact */
	async skipPhoneCall(
		campaignId: number,
		pcId: number,
		attemptId: number,
	): Promise<{ status: string }> {
		return this.post(
			ApiService.Endpoint.PhoneCallSkip(campaignId, pcId, attemptId),
			{},
		);
	}

	/** Get queue stats */
	async getPhoneQueueStats(
		campaignId: number,
		pcId: number,
	): Promise<QueueStats> {
		return this.get<QueueStats>(
			ApiService.Endpoint.PhoneCallStats(campaignId, pcId),
		);
	}

	// -----------------------------------------------------------------------
	// Phone Banking - Results / Metrics
	// -----------------------------------------------------------------------

	/** Get full campaign results (leaderboard + disposition breakdown) */
	async getPhoneCampaignResults(
		campaignId: number,
		pcId: number,
	): Promise<PhoneCampaignResultsResponse> {
		return this.get<PhoneCampaignResultsResponse>(
			ApiService.Endpoint.PhoneCampaignResults(campaignId, pcId),
		);
	}

	/** Get condensed campaign results summary */
	async getPhoneCampaignResultsSummary(
		campaignId: number,
		pcId: number,
	): Promise<PhoneCampaignSummaryResponse> {
		return this.get<PhoneCampaignSummaryResponse>(
			ApiService.Endpoint.PhoneCampaignResultsSummary(campaignId, pcId),
		);
	}

	/** Get phone call history for a contact */
	async getContactPhoneHistory(
		campaignId: number,
		contactId: number,
	): Promise<ContactPhoneHistoryResponse> {
		return this.get<ContactPhoneHistoryResponse>(
			ApiService.Endpoint.ContactPhoneHistory(campaignId, contactId),
		);
	}

	// ── Sequential Interview ────────────────────────────────────────────

	/** Get sequential interview status (resume support) */
	getSequentialInterviewStatus(campaignSlug: string) {
		return this.get(`/campaigns/${campaignSlug}/interview/sequential/status`);
	}

	/** Start a new sequential interview session */
	startSequentialInterview(campaignSlug: string) {
		return this.post(`/campaigns/${campaignSlug}/interview/sequential/start`);
	}

	/** Save an answer for a sequential interview question */
	saveSequentialAnswer(
		campaignSlug: string,
		data: {
			question_index: number;
			answer_text: string;
			answer_source: "voice" | "text";
		},
	) {
		return this.post(
			`/campaigns/${campaignSlug}/interview/sequential/answer`,
			data,
		);
	}

	/** Pause a sequential interview session */
	pauseSequentialInterview(campaignSlug: string) {
		return this.post(`/campaigns/${campaignSlug}/interview/sequential/pause`);
	}

	/** Complete a sequential interview */
	completeSequentialInterview(campaignSlug: string) {
		return this.post(
			`/campaigns/${campaignSlug}/interview/sequential/complete`,
		);
	}

	/** Get a single-use ElevenLabs token for client-side STT */
	getSequentialSTTToken(campaignSlug: string): Promise<{ token: string }> {
		return this.post(
			`/campaigns/${campaignSlug}/interview/sequential/stt-token`,
		) as Promise<{ token: string }>;
	}

	/** Stream TTS audio for a sequential interview question (returns raw Response for NDJSON parsing) */
	async streamSequentialTTS(
		campaignSlug: string,
		text: string,
		voiceId?: string,
	): Promise<Response> {
		const token = await authService.getAuthToken();
		return fetch(
			`${API_BASE_URL}/campaigns/${campaignSlug}/interview/sequential/tts`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({ text, voice_id: voiceId }),
			},
		);
	}

	// -----------------------------------------------------------------------
	// Ballot Access
	// -----------------------------------------------------------------------

	async getBallotAccessRequirements(
		campaignId: number,
	): Promise<SignatureRequirement | null> {
		// 204 = no requirement record yet; base request() returns undefined for 204
		const result = await this.get<SignatureRequirement | undefined>(
			`/campaigns/${campaignId}/ballot-access/requirements`,
		);
		return result ?? null;
	}

	lookupBallotAccessRequirements(
		campaignId: number,
		data: LookupRequirementsRequest,
	): Promise<LookupRequirementsResponse> {
		return this.post<LookupRequirementsResponse>(
			`/campaigns/${campaignId}/ballot-access/requirements/lookup`,
			data,
		);
	}

	updateBallotAccessRequirements(
		campaignId: number,
		data: UpdateRequirementsRequest,
	): Promise<SignatureRequirement> {
		return this.put<SignatureRequirement>(
			`/campaigns/${campaignId}/ballot-access/requirements`,
			data,
		);
	}

	updateBallotAccessRequirementsStatus(
		campaignId: number,
		data: UpdateRequirementsStatusRequest,
	): Promise<SignatureRequirement> {
		return this.patch<SignatureRequirement>(
			`/campaigns/${campaignId}/ballot-access/requirements/status`,
			data,
		);
	}

	getBallotAccessProgress(campaignId: number): Promise<BallotAccessProgress> {
		return this.get<BallotAccessProgress>(
			`/campaigns/${campaignId}/ballot-access/progress`,
		);
	}

	getBallotAccessDrives(campaignId: number): Promise<PetitionDrive[]> {
		return this.get<PetitionDrive[]>(
			`/campaigns/${campaignId}/ballot-access/drives`,
		);
	}

	createBallotAccessDrive(
		campaignId: number,
		data: DriveCreateRequest,
	): Promise<PetitionDrive> {
		return this.post<PetitionDrive>(
			`/campaigns/${campaignId}/ballot-access/drives`,
			data,
		);
	}

	updateBallotAccessDrive(
		campaignId: number,
		driveId: number,
		data: DriveUpdateRequest,
	): Promise<PetitionDrive> {
		return this.patch<PetitionDrive>(
			`/campaigns/${campaignId}/ballot-access/drives/${driveId}`,
			data,
		);
	}

	deleteBallotAccessDrive(campaignId: number, driveId: number): Promise<void> {
		return this.delete<void>(
			`/campaigns/${campaignId}/ballot-access/drives/${driveId}`,
		);
	}

	getBallotAccessDrive(
		campaignId: number,
		driveId: number,
	): Promise<PetitionDriveDetail> {
		return this.get<PetitionDriveDetail>(
			`/campaigns/${campaignId}/ballot-access/drives/${driveId}`,
		);
	}

	getSigners(
		campaignId: number,
		driveId: number,
		validationStatus?: string,
		page = 1,
		perPage = 25,
	): Promise<SignerListResponse> {
		const params = new URLSearchParams({
			page: String(page),
			per_page: String(perPage),
		});
		if (validationStatus) params.set("validation_status", validationStatus);
		return this.get<SignerListResponse>(
			`/campaigns/${campaignId}/ballot-access/drives/${driveId}/signers?${params}`,
		);
	}

	logSigner(
		campaignId: number,
		driveId: number,
		data: SignerCreateRequest,
	): Promise<PetitionSigner> {
		return this.post<PetitionSigner>(
			`/campaigns/${campaignId}/ballot-access/drives/${driveId}/signers`,
			data,
		);
	}

	batchValidateSigners(
		campaignId: number,
		driveId: number,
	): Promise<ValidationResult> {
		return this.post<ValidationResult>(
			`/campaigns/${campaignId}/ballot-access/drives/${driveId}/signers/validate`,
			{},
		);
	}

	deleteSigner(
		campaignId: number,
		driveId: number,
		signerId: number,
	): Promise<void> {
		return this.delete<void>(
			`/campaigns/${campaignId}/ballot-access/drives/${driveId}/signers/${signerId}`,
		);
	}

	/** Get follow-up questions for incomplete interview answers */
	async getFollowUpQuestions(campaignSlug: string) {
		return this.get<{
			questions: Array<{
				question_index: number;
				original_question: string;
				original_answer: string;
				flag_reason: string;
				flag_reason_display: string;
				follow_up_prompt: string;
				category: string;
			}>;
			total_flagged: number;
			interview_id: number;
		}>(ApiService.Endpoint.InterviewFollowUpQuestions(campaignSlug));
	}

	/** Submit follow-up answers for flagged questions */
	async submitFollowUpAnswers(
		campaignSlug: string,
		answers: Array<{
			question_index: number;
			answer_text: string;
			skipped: boolean;
		}>,
	) {
		return this.post<{
			saved_count: number;
			skipped_count: number;
			message: string;
		}>(ApiService.Endpoint.InterviewFollowUpAnswers(campaignSlug), {
			answers,
		});
	}

	/** Upload an interview attachment file */
	async uploadInterviewAttachment(
		campaignSlug: string,
		file: File,
	): Promise<{
		id: number;
		campaign_id: number;
		filename: string;
		category: string;
		s3_key: string;
		file_size_bytes: number;
		uploaded_at: string;
	}> {
		const token = await authService.getAuthToken();
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetch(
			`${this.baseURL}${ApiService.Endpoint.InterviewAttachmentUpload(campaignSlug)}`,
			{
				method: "POST",
				headers: {
					...(token && { Authorization: `Bearer ${token}` }),
				},
				body: formData,
			},
		);

		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new Error(
				errorMessageFromBody(body, response.statusText, response.status),
			);
		}

		return response.json();
	}

	/** List interview attachments for a campaign */
	async listInterviewAttachments(campaignSlug: string) {
		return this.get<{
			attachments: Array<{
				id: number;
				filename: string;
				category: string;
				s3_key: string;
				file_size_bytes: number;
				content_type: string;
				uploaded_at: string;
			}>;
			total: number;
			campaign_id: number;
		}>(ApiService.Endpoint.InterviewAttachmentList(campaignSlug));
	}

	/** Delete an interview attachment */
	async deleteInterviewAttachment(campaignSlug: string, attachmentId: number) {
		return this.delete<{ message: string; id: number }>(
			ApiService.Endpoint.InterviewAttachmentDelete(campaignSlug, attachmentId),
		);
	}

	// -----------------------------------------------------------------------
	// Volunteers (PRD-43)
	// -----------------------------------------------------------------------

	/** List campaign volunteers with filters + pagination */
	async getVolunteers(
		campaignId: number,
		params: VolunteerListParams = {},
	): Promise<PaginatedVolunteerResponse> {
		const sp = new URLSearchParams();
		if (params.search) sp.set("search", params.search);
		if (params.status) sp.set("status", params.status);
		if (params.source) sp.set("source", params.source);
		if (params.house_party_status)
			sp.set("house_party_status", params.house_party_status);
		if (params.wants_yard_sign != null)
			sp.set("wants_yard_sign", String(params.wants_yard_sign));
		if (params.wants_bumper_sticker != null)
			sp.set("wants_bumper_sticker", String(params.wants_bumper_sticker));
		if (params.bumper_sticker_delivered != null)
			sp.set(
				"bumper_sticker_delivered",
				String(params.bumper_sticker_delivered),
			);
		if (params.wants_to_host_house_party != null)
			sp.set(
				"wants_to_host_house_party",
				String(params.wants_to_host_house_party),
			);
		if (params.wants_to_help_raise_money != null)
			sp.set(
				"wants_to_help_raise_money",
				String(params.wants_to_help_raise_money),
			);
		if (params.subscribed_to_updates != null)
			sp.set("subscribed_to_updates", String(params.subscribed_to_updates));
		if (params.yard_sign_delivered != null)
			sp.set("yard_sign_delivered", String(params.yard_sign_delivered));
		if (params.page != null) sp.set("page", String(params.page));
		if (params.per_page != null) sp.set("per_page", String(params.per_page));
		const q = sp.toString();
		return this.get<PaginatedVolunteerResponse>(
			`${API.volunteers.list(campaignId)}${q ? `?${q}` : ""}`,
		);
	}

	/** Total volunteer count for a campaign (drives the sidebar entry) */
	async getVolunteerCount(campaignId: number): Promise<{ count: number }> {
		return this.get<{ count: number }>(API.volunteers.count(campaignId));
	}

	/** Fetch a single volunteer profile */
	async getVolunteer(
		campaignId: number,
		volunteerId: number,
	): Promise<VolunteerProfile> {
		return this.get<VolunteerProfile>(
			API.volunteers.detail(campaignId, volunteerId),
		);
	}

	/** Create a volunteer manually */
	async createVolunteer(
		campaignId: number,
		data: VolunteerProfileCreate,
	): Promise<VolunteerProfile> {
		return this.post<VolunteerProfile>(API.volunteers.list(campaignId), data);
	}

	/** Partially update a volunteer profile */
	async updateVolunteer(
		campaignId: number,
		volunteerId: number,
		data: VolunteerProfileUpdate,
	): Promise<VolunteerProfile> {
		return this.patch<VolunteerProfile>(
			API.volunteers.detail(campaignId, volunteerId),
			data,
		);
	}

	/** Delete a volunteer profile */
	async deleteVolunteer(
		campaignId: number,
		volunteerId: number,
	): Promise<void> {
		return this.delete(API.volunteers.detail(campaignId, volunteerId));
	}

	/** Upload a CSV to kick off an async volunteer import */
	async importVolunteers(
		campaignId: number,
		file: File,
	): Promise<VolunteerImportResponse> {
		const token = await authService.getAuthToken();
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetch(
			`${this.baseURL}${API.volunteers.import(campaignId)}`,
			{
				method: "POST",
				headers: {
					...(token && { Authorization: `Bearer ${token}` }),
				},
				body: formData,
			},
		);

		if (!response.ok) {
			const body = await response.json().catch(() => ({}));
			throw new Error(
				errorMessageFromBody(body, response.statusText, response.status),
			);
		}

		return response.json();
	}

	/** Download the volunteers CSV export as a Blob */
	async exportVolunteers(campaignId: number): Promise<Blob> {
		return this.getBlob(API.volunteers.export(campaignId));
	}

	/** Poll the status of an async volunteer CSV import job */
	async getVolunteerImportJob(
		campaignId: number,
		jobId: number,
	): Promise<VolunteerImportJobStatus> {
		return this.get<VolunteerImportJobStatus>(
			API.volunteers.importJob(campaignId, jobId),
		);
	}

	// -------------------------------------------------------------------------
	// Prepaid Billing (PRD 45 — Phase 1 read surfaces)
	// -------------------------------------------------------------------------

	/** Wallet overview: balance, tier, runway, auto-reload state, rates. */
	async getBillingWallet(campaignId: number): Promise<WalletResponse> {
		return this.get<WalletResponse>(
			ApiService.Endpoint.BillingWallet(campaignId),
		);
	}

	// --- Unified Outreach (PRD 46) ---

	/** Sub-second contactable-after-caps count for the audience builder. */
	async getOutreachAudienceCount(
		campaignId: number,
		body: AudienceCountRequest,
	): Promise<AudienceCountResponse> {
		return this.post<AudienceCountResponse>(
			ApiService.Endpoint.OutreachAudienceCount(campaignId),
			body,
		);
	}

	/** Phase 4: parse a plain-words audience description into a definition. */
	async parseOutreachAudience(
		campaignId: number,
		description: string,
	): Promise<NlParseResponse> {
		return this.post<NlParseResponse>(
			ApiService.Endpoint.OutreachAudienceParse(campaignId),
			{ description },
		);
	}

	async refineOutreachAudience(
		campaignId: number,
		instruction: string,
		definition: AudienceDefinition,
	): Promise<NlRefineResponse> {
		return this.post<NlRefineResponse>(
			ApiService.Endpoint.OutreachAudienceRefine(campaignId),
			{ instruction, definition },
		);
	}

	async getOutreachAudienceSuggestions(
		campaignId: number,
		goalText: string,
		channel?: string,
	): Promise<SuggestionChip[]> {
		return this.get<SuggestionChip[]>(
			ApiService.Endpoint.OutreachAudienceSuggestions(campaignId),
			channel ? { goal_text: goalText, channel } : { goal_text: goalText },
		);
	}

	/** Saved audiences (Action Groups successor). */
	async listOutreachAudiences(
		campaignId: number,
		includeArchived = false,
	): Promise<OutreachAudience[]> {
		return this.get<OutreachAudience[]>(
			ApiService.Endpoint.OutreachAudiences(campaignId),
			includeArchived ? { include_archived: true } : undefined,
		);
	}

	async createOutreachAudience(
		campaignId: number,
		body: OutreachAudienceCreate,
	): Promise<OutreachAudience> {
		return this.post<OutreachAudience>(
			ApiService.Endpoint.OutreachAudiences(campaignId),
			body,
		);
	}

	async getOutreachAudience(
		campaignId: number,
		audienceId: number,
	): Promise<OutreachAudience> {
		return this.get<OutreachAudience>(
			ApiService.Endpoint.OutreachAudience(campaignId, audienceId),
		);
	}

	async updateOutreachAudience(
		campaignId: number,
		audienceId: number,
		body: OutreachAudienceUpdate,
	): Promise<OutreachAudience> {
		return this.patch<OutreachAudience>(
			ApiService.Endpoint.OutreachAudience(campaignId, audienceId),
			body,
		);
	}

	/** Step 1 clarification dive (objective + gap-aware questions). */
	async clarifyOutreachGoal(
		campaignId: number,
		goalText: string,
	): Promise<ClarifyResponse> {
		return this.post<ClarifyResponse>(
			ApiService.Endpoint.OutreachGoalClarify(campaignId),
			{ goal_text: goalText },
		);
	}

	async listOutreachCampaigns(campaignId: number): Promise<OutreachCampaign[]> {
		return this.get<OutreachCampaign[]>(
			ApiService.Endpoint.OutreachCampaigns(campaignId),
		);
	}

	// --- Campaigns (OutreachGoal container) — PRD 53 ---
	async listOutreachGoals(campaignId: number): Promise<OutreachGoal[]> {
		return this.get<OutreachGoal[]>(
			ApiService.Endpoint.OutreachGoals(campaignId),
		);
	}

	async getOutreachGoal(
		campaignId: number,
		goalId: number,
	): Promise<OutreachGoal> {
		return this.get<OutreachGoal>(
			ApiService.Endpoint.OutreachGoal(campaignId, goalId),
		);
	}

	async createOutreachGoal(
		campaignId: number,
		body: OutreachGoalCreate,
	): Promise<OutreachGoal> {
		return this.post<OutreachGoal>(
			ApiService.Endpoint.OutreachGoals(campaignId),
			body,
		);
	}

	async updateOutreachGoal(
		campaignId: number,
		goalId: number,
		body: OutreachGoalUpdate,
	): Promise<OutreachGoal> {
		return this.patch<OutreachGoal>(
			ApiService.Endpoint.OutreachGoal(campaignId, goalId),
			body,
		);
	}

	/** Add a new single-channel action to a Campaign (inherits the campaign audience). */
	async addActionToGoal(
		campaignId: number,
		goalId: number,
		body: AddSendToGoalRequest = {},
	): Promise<OutreachCampaign> {
		return this.post<OutreachCampaign>(
			ApiService.Endpoint.OutreachGoalSends(campaignId, goalId),
			body,
		);
	}

	async createOutreachCampaign(
		campaignId: number,
		body: OutreachCampaignCreate,
	): Promise<OutreachCampaign> {
		return this.post<OutreachCampaign>(
			ApiService.Endpoint.OutreachCampaigns(campaignId),
			body,
		);
	}

	async getOutreachCampaign(
		campaignId: number,
		ocId: number,
	): Promise<OutreachCampaign> {
		return this.get<OutreachCampaign>(
			ApiService.Endpoint.OutreachCampaign(campaignId, ocId),
		);
	}

	async updateOutreachCampaign(
		campaignId: number,
		ocId: number,
		body: OutreachCampaignUpdate,
	): Promise<OutreachCampaign> {
		return this.patch<OutreachCampaign>(
			ApiService.Endpoint.OutreachCampaign(campaignId, ocId),
			body,
		);
	}

	/** Delete a draft outreach action (server 409s if it has already launched). */
	async deleteOutreachCampaign(
		campaignId: number,
		ocId: number,
	): Promise<void> {
		return this.delete(ApiService.Endpoint.OutreachCampaign(campaignId, ocId));
	}

	/** Step 2 channel-shaped draft (regenerate when `direction` is supplied). */
	async draftOutreachMessage(
		campaignId: number,
		ocId: number,
		body: DraftRequest,
	): Promise<DraftResponse> {
		return this.post<DraftResponse>(
			ApiService.Endpoint.OutreachCampaignDraft(campaignId, ocId),
			body,
		);
	}

	async getOutreachReview(
		campaignId: number,
		ocId: number,
	): Promise<ReviewResponse> {
		return this.get<ReviewResponse>(
			ApiService.Endpoint.OutreachCampaignReview(campaignId, ocId),
		);
	}

	async launchOutreachCampaign(
		campaignId: number,
		ocId: number,
		acknowledgedPlaceholders: string[] = [],
	): Promise<OutreachCampaign> {
		return this.post<OutreachCampaign>(
			ApiService.Endpoint.OutreachCampaignLaunch(campaignId, ocId),
			{ acknowledged_placeholders: acknowledgedPlaceholders },
		);
	}

	async sendOutreachTestMessage(
		campaignId: number,
		ocId: number,
		phone: string,
	): Promise<TestSendResponse> {
		return this.post<TestSendResponse>(
			ApiService.Endpoint.OutreachCampaignTestSend(campaignId, ocId),
			{ phone },
		);
	}

	async sendOutreachTestEmail(
		campaignId: number,
		ocId: number,
		toEmail: string,
	): Promise<TestSendResponse> {
		return this.post<TestSendResponse>(
			ApiService.Endpoint.OutreachCampaignTestSendEmail(campaignId, ocId),
			{ to_email: toEmail },
		);
	}

	/**
	 * Upload an image for the email composer. Returns an absolute, permanently
	 * public URL (served by the backend) safe to embed in the email body.
	 */
	async uploadEmailImage(
		campaignId: number,
		file: File,
	): Promise<{ url: string; id: string }> {
		const formData = new FormData();
		formData.append("file", file);
		const result = await this.postFormData<{ id: string; path: string }>(
			ApiService.Endpoint.EmailImageUpload(campaignId),
			formData,
		);
		return { id: result.id, url: `${this.baseURL}${result.path}` };
	}

	/**
	 * Render an outreach email body as recipients will see it (template chrome,
	 * disclaimer/unsubscribe footer, sample merge values). Stateless — reflects
	 * the live composer body without a save.
	 */
	async getOutreachEmailPreview(
		campaignId: number,
		subject: string,
		body: string,
	): Promise<{ html: string; subject: string }> {
		return this.post<{ html: string; subject: string }>(
			ApiService.Endpoint.OutreachEmailPreview(campaignId),
			{ subject, body },
		);
	}

	async duplicateOutreachCampaign(
		campaignId: number,
		ocId: number,
	): Promise<OutreachCampaign> {
		return this.post<OutreachCampaign>(
			ApiService.Endpoint.OutreachCampaignDuplicate(campaignId, ocId),
		);
	}

	/** Wallet spend (PRD 45) attributed to this campaign's channel row. */
	async getOutreachCostToDate(
		campaignId: number,
		ocId: number,
	): Promise<OutreachCostToDate> {
		return this.get<OutreachCostToDate>(
			ApiService.Endpoint.OutreachCampaignCostToDate(campaignId, ocId),
		);
	}

	// -------------------------------------------------------------------------
	// Smart Links (PRD 47 — per-blast click tracking on the SMS channels)
	// -------------------------------------------------------------------------

	/** Tracking domains an admin has configured for this campaign. */
	async getSmartLinkDomains(campaignId: number): Promise<string[]> {
		return this.get<string[]>(
			ApiService.Endpoint.OutreachSmartLinkDomains(campaignId),
		);
	}

	/** Current Smart Links state for a draft (detected URLs + generated links). */
	async getSmartLinks(
		campaignId: number,
		ocId: number,
	): Promise<SmartLinksResponse> {
		return this.get<SmartLinksResponse>(
			ApiService.Endpoint.OutreachSmartLinks(campaignId, ocId),
		);
	}

	/** Generate smart links from the URLs in the draft (the "Generate" action). */
	async syncSmartLinks(
		campaignId: number,
		ocId: number,
	): Promise<SmartLinksResponse> {
		return this.post<SmartLinksResponse>(
			ApiService.Endpoint.OutreachSmartLinksSync(campaignId, ocId),
		);
	}

	/** Set the per-blast tracking domain. */
	async setSmartLinkDomain(
		campaignId: number,
		ocId: number,
		domain: string,
	): Promise<SmartLinksResponse> {
		return this.patch<SmartLinksResponse>(
			ApiService.Endpoint.OutreachSmartLinksDomain(campaignId, ocId),
			{ domain },
		);
	}

	/** Post-launch click metrics (total clicks, CTR, per-link breakdown). */
	async getSmartLinkMetrics(
		campaignId: number,
		ocId: number,
	): Promise<SmartLinkMetricsResponse> {
		return this.get<SmartLinkMetricsResponse>(
			ApiService.Endpoint.OutreachSmartLinksMetrics(campaignId, ocId),
		);
	}

	/** Current rate schedule (per-unit text/phone rates). */
	async getBillingRates(campaignId: number): Promise<BillingRate[]> {
		const res = await this.get<{ rates: BillingRate[] }>(
			ApiService.Endpoint.BillingRates(campaignId),
		);
		return res.rates;
	}

	/** Paginated, filterable usage ledger + daily aggregates + totals. */
	async getBillingUsage(
		campaignId: number,
		params: UsageQueryParams = {},
	): Promise<UsageResponse> {
		return this.get<UsageResponse>(
			ApiService.Endpoint.BillingUsage(campaignId),
			params as Record<string, unknown>,
		);
	}

	/** Download the filtered usage ledger as a CSV Blob (injection-safe server-side). */
	async exportBillingUsage(
		campaignId: number,
		params: UsageQueryParams = {},
	): Promise<Blob> {
		const search = new URLSearchParams();
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined && value !== null) {
				search.set(key, String(value));
			}
		}
		const qs = search.toString();
		const base = ApiService.Endpoint.BillingUsageExport(campaignId);
		return this.getBlob(qs ? `${base}?${qs}` : base);
	}

	// -------------------------------------------------------------------------
	// Prepaid Billing (PRD 45 — Phase 2: top-up + payment methods)
	// -------------------------------------------------------------------------

	/**
	 * Create a Stripe PaymentIntent for a top-up. Crediting is webhook-driven —
	 * confirming this intent client-side does NOT credit the wallet; the backend
	 * credits on `payment_intent.succeeded`.
	 */
	async createBillingTopUpIntent(
		campaignId: number,
		data: { amount_micros: number; payment_method_id?: string },
	): Promise<TopUpIntentResponse> {
		return this.post<TopUpIntentResponse>(
			ApiService.Endpoint.BillingTopUpIntent(campaignId),
			data,
		);
	}

	/** Create a Stripe SetupIntent for saving a new card (no charge). */
	async createBillingSetupIntent(
		campaignId: number,
	): Promise<SetupIntentResponse> {
		return this.post<SetupIntentResponse>(
			ApiService.Endpoint.BillingSetupIntent(campaignId),
		);
	}

	/** List saved cards (source of truth is Stripe). Unwraps the envelope. */
	async getBillingPaymentMethods(campaignId: number): Promise<PaymentMethod[]> {
		const res = await this.get<{ payment_methods: PaymentMethod[] }>(
			ApiService.Endpoint.BillingPaymentMethods(campaignId),
		);
		return res.payment_methods;
	}

	/** Set a saved card as the default payment method. */
	async setDefaultBillingPaymentMethod(
		campaignId: number,
		pmId: string,
	): Promise<{ status: string }> {
		return this.post<{ status: string }>(
			ApiService.Endpoint.BillingPaymentMethodDefault(campaignId, pmId),
		);
	}

	/** Detach (remove) a saved card. */
	async removeBillingPaymentMethod(
		campaignId: number,
		pmId: string,
	): Promise<void> {
		return this.delete<void>(
			ApiService.Endpoint.BillingPaymentMethodDetach(campaignId, pmId),
		);
	}

	// -------------------------------------------------------------------------
	// Prepaid Billing (PRD 45 — Phase 3: pre-flight estimate + send-gate)
	// -------------------------------------------------------------------------

	/**
	 * Pre-flight cost estimate for a blast (BILL-11). Returns the estimated
	 * cost, current balance, projected balance-after (may be negative), and a
	 * `sufficient` gate flag. Drives the low-balance launch alert.
	 */
	async getBillingSendEstimate(
		campaignId: number,
		data: SendEstimateRequest,
	): Promise<SendEstimate> {
		return this.post<SendEstimate>(
			ApiService.Endpoint.BillingEstimate(campaignId),
			data,
		);
	}

	/** Update auto-reload settings + warning/send-gate thresholds (BILL-06). */
	async updateBillingAutoReload(
		campaignId: number,
		data: AutoReloadUpdateRequest,
	): Promise<WalletResponse> {
		return this.put<WalletResponse>(
			ApiService.Endpoint.BillingAutoReload(campaignId),
			data,
		);
	}
}

export const apiService = new ApiService(authService, () => {
	authService.clearTokens();
	router.navigate({ to: "/login" });
});
